import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import { PageHeader, ProColumns } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { Button, Tabs, Popconfirm, Card, Row, Col } from "antd";
import { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import MMNavigationRoutes from "../../utils/navigationRoutes";
import { Mail, Edit2, Trash, Plus } from "lucide-react";
import MMLogo from "@/projects/Civi_Circle_Admin/assets/eventimg.jpg";
import eyeIcon from "@/projects/Civi_Circle_Admin/assets/eye.png";
import calendarBadgeIcon from "@/projects/Civi_Circle_Admin/assets/calendar.png";
import clockBadgeIcon from "@/projects/Civi_Circle_Admin/assets/clock.png";
import { DynamicModuleService } from "@/services/APIs";
import EventService from "@/services/APIs/EventService";
import { notify } from "@/components/common/Notification";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import CustomButton from "@/components/common/Button";
import WorkflowStatus from "@/components/WorkflowStatus";
import ActiveToggleButton from "@/components/ActiveToggleButton";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Helper function to calculate days left
const calculateDaysLeft = (endDate: string | null | undefined): number => {
  if (!endDate) {
    return 0;
  }
  try {
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  } catch (error) {
    console.error("Error calculating days left:", error);
    return 0;
  }
};

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const basicInfoSchema = "Event Information";
  const historyMilestonesSchema = "MM_Volunteers";
  const membershipTypesSchema = "Add Ticket";
  const noticeBoardSchema = "MM_Qr_Check_In";
  const contactLocationsSchema = "MM_Agenda_Sessions";
  const committeeLeadershipSchema = "MM_Communication";
  const documentsSchema = "mm_documents";
  const hoursstatus = "MM_Donation";
  const photoGallerySchema = "MM_Sponsorship";
  const addphotogallery = "add_photo_gallery";
  const bankingSchema = "Banking";
  const faqSchema = "mm_faq";
  const sponsorshipSchema = "MM Sponsor";
  const [searchParams] = useSearchParams();
  <style>
    {`
  .responsive-card-list {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    justify-content: flex-start;
    width: 100%;
  }
  .responsive-card {
    width: 100%;
    max-width: 380px;
    min-width: 260px;
    margin-bottom: 16px;
  }
  @media (max-width: 700px) {
    .responsive-card-list {
      flex-direction: column;
      gap: 16px;
    }
    .responsive-card {
      max-width: none;
      min-width: 0;
      margin-right: 0;
    }
  }
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`}
  </style>;
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // const [editingRecord, setEditingRecord] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Individual form states for each schema
  const [editingSchema, setEditingSchema] = useState<string>("");
  const [editingRecordId, setEditingRecordId] = useState<string>("");

  // State to control add photo gallery form visibility
  const [showAddPhotoGallery, setShowAddPhotoGallery] =
    useState<boolean>(false);

  // State to track event price for conditional field visibility
  const [, setEventPrice] = useState<string>("");

  // Individual forms for each tab
  const formOverview = useMemo(() => createForm({}), []);
  const formHistoryMilestones = useMemo(() => createForm({}), []);
  const formMembershipTypes = useMemo(() => createForm({}), []);
  const formContactLocations = useMemo(() => createForm({}), []);
  const formHoursStatus = useMemo(() => createForm({}), []);
  const formPhotoGallery = useMemo(() => createForm({}), []);
  const formAddPhotoGallery = useMemo(() => createForm({}), []);
  const formCommitteeLeadership = useMemo(() => createForm({}), []);
  const formNoticeBoard = useMemo(() => createForm({}), []);
  const formBanking = useMemo(() => createForm({}), []);
  const formDocuments = useMemo(() => createForm({}), []);
  const formFAQ = useMemo(() => createForm({}), []);
  const formSponsorship = useMemo(() => createForm({}), []);

  // Individual form refs for each tab
  const formOverviewRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const formHistoryMilestonesRef = useRef<{ handleSave: () => Promise<void> }>(
    null
  );
  const formMembershipTypesRef = useRef<{ handleSave: () => Promise<void> }>(
    null
  );
  const formContactLocationsRef = useRef<{ handleSave: () => Promise<void> }>(
    null
  );
  const formHoursStatusRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const formPhotoGalleryRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const formAddPhotoGalleryRef = useRef<{ handleSave: () => Promise<void> }>(
    null
  );
  const formCommitteeLeadershipRef = useRef<{
    handleSave: () => Promise<void>;
  }>(null);
  const formNoticeBoardRef = useRef<{ handleSave: () => Promise<void> }>(null);
  // const formBankingRef = useRef<{ handleSave: () => Promise<void> }>(null);
  // const formDocumentsRef = useRef<{ handleSave: () => Promise<void> }>(null);
  // const formFAQRef = useRef<{ handleSave: () => Promise<void> }>(null);
  // const formSponsorshipRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const fnameFromUrl = searchParams.get("fname");
  const tabLabelClassName = "text-sm text-foreground";

  const infoIconWrapperClassName =
    "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary shadow-md";

  const infoIconImageStyle = {
    width: "18px",
    height: "18px",
    objectFit: "contain" as const,
    filter: "brightness(0) invert(1)",
  };

  // Alternative: Use a callback to get the created ID
  const handleSaveOrganization = async () => {
    try {
      // Validate form first
      await formOverview.validate();
      const formValues = formOverview.values;

      const response = await DynamicModuleService.createItem(
        basicInfoSchema ?? "",
        formValues
      );

      if (response?.id) {
        navigate("/mm" + MMNavigationRoutes.EDIT_EVENT(response.id));
        // notify.success("Organization created successfully");
      } else {
        notify.error("Failed to get organization ID after creation");
      }
    } catch (error) {
      console.error("Save error:", error);
      // notify.error("Please fill in all required fields correctly");
    }
  };

  useEffect(() => {
    if (isEditMode && id) {
      // Set initial values for all forms
      formOverview.setInitialValues({
        id: id,
        jdid: id,
      });
      formOverview.setValues({
        id: id,
        jdid: id,
      });

      formHistoryMilestones.setInitialValues({
        jdid: id,
      });
      formHistoryMilestones.setValues({
        jdid: id,
        is_active: true,
      });

      formMembershipTypes.setInitialValues({
        jdid: id,
      });
      formMembershipTypes.setValues({
        jdid: id,
      });

      formContactLocations.setInitialValues({
        jdid: id,
      });
      formContactLocations.setValues({
        jdid: id,
        is_active: true,
      });

      formHoursStatus.setInitialValues({
        jdid: id,
      });
      formHoursStatus.setValues({
        jdid: id,
        is_active: true,
      });

      formPhotoGallery.setInitialValues({
        jdid: id,
      });
      formPhotoGallery.setValues({
        jdid: id,
      });

      formAddPhotoGallery.setInitialValues({
        jdid: id,
      });
      formAddPhotoGallery.setValues({
        jdid: id,
      });

      formCommitteeLeadership.setInitialValues({
        jdid: id,
      });
      formCommitteeLeadership.setValues({
        jdid: id,
      });

      formNoticeBoard.setInitialValues({
        jdid: id,
      });
      formNoticeBoard.setValues({
        jdid: id,
      });

      formBanking.setInitialValues({
        jdid: id,
      });
      formBanking.setValues({
        jdid: id,
      });

      formDocuments.setInitialValues({
        jdid: id,
      });
      formDocuments.setValues({
        jdid: id,
      });

      formFAQ.setInitialValues({
        jdid: id,
      });
      formFAQ.setValues({
        jdid: id,
      });

      formSponsorship.setInitialValues({
        jdid: id,
      });
      formSponsorship.setValues({
        jdid: id,
      });
    }
  }, [
    id,
    isEditMode,
    formOverview,
    formHistoryMilestones,
    formMembershipTypes,
    formContactLocations,
    formHoursStatus,
    formPhotoGallery,
    formAddPhotoGallery,
    formCommitteeLeadership,
    formNoticeBoard,
    formBanking,
    formDocuments,
    formFAQ,
    formSponsorship,
  ]);

  const getTicketCategoryLabel = (ticket: any): string | null => {
    if (!ticket) {
      return null;
    }
    const label =
      ticket.ticket_category ??
      ticket.ticket_category_name ??
      ticket.ticket_category_title ??
      ticket.ticket_name ??
      ticket.category ??
      ticket.membership_type ??
      ticket.name;
    return label !== null ? String(label) : null;
  };

  const getDonationCategoryLabel = (donation: any): string | null => {
    if (!donation) {
      return null;
    }
    const label =
      donation.category ??
      donation.donation_category ??
      donation.donationtitle ??
      donation.donation_title ??
      donation.title;
    return label !== null ? String(label) : null;
  };

  // Handle edit ticket - Load data into form for inline editing
  const handleEditTicket = (ticketData: any) => {
    console.log("Edit ticket data:", ticketData); // Debug log

    // Load the ticket data into the form with proper field mapping
    const formData = {
      id: ticketData.id,
      category: ticketData.category,
      benefits: ticketData.benefits,
      price: ticketData.price,
      ticket: ticketData.ticket,
      is_active: ticketData.is_active,
      jdid: id, // Ensure jdid is set to the current chapter ID
    };

    console.log("Form data being set:", formData); // Debug log

    // Set values for the form
    formMembershipTypes.setValues(formData);
    // Optionally clear errors but don't reset the whole form/graph
    formMembershipTypes.clearErrors();

    // Set editing state for inline editing
    setEditingSchema(membershipTypesSchema);
    setEditingRecordId(ticketData.id);
    setIsEditing(true);
    setSelectedTicketCategoryFilter(getTicketCategoryLabel(ticketData));
  };

  // Handle edit item - Load data into form instead of navigating
  const handleEdit = (record: any, schemaName: string) => {
    // Special handling for ticket editing
    if (schemaName === membershipTypesSchema) {
      handleEditTicket(record);
      return;
    }

    setEditingSchema(schemaName);
    setEditingRecordId(record.id);
    setIsEditing(true);

    // Load the record data into the appropriate form based on schema
    const formData = {
      ...record,
      jdid: id, // Ensure jdid is set to the current chapter ID
    };

    const setupFormForEdit = (form: any) => {
      form.setInitialValues(formData);
      form.setValues(formData);
      form.clearErrors();
    };

    if (schemaName === historyMilestonesSchema) {
      setupFormForEdit(formHistoryMilestones);
    } else if (schemaName === contactLocationsSchema) {
      setupFormForEdit(formContactLocations);
    } else if (schemaName === hoursstatus) {
      setupFormForEdit(formHoursStatus);
    } else if (schemaName === photoGallerySchema) {
      setupFormForEdit(formPhotoGallery);
    } else if (schemaName === addphotogallery) {
      setupFormForEdit(formAddPhotoGallery);
    } else if (schemaName === committeeLeadershipSchema) {
      setupFormForEdit(formCommitteeLeadership);
    } else if (schemaName === noticeBoardSchema) {
      setupFormForEdit(formNoticeBoard);
    } else if (schemaName === bankingSchema) {
      setupFormForEdit(formBanking);
    } else if (schemaName === documentsSchema) {
      setupFormForEdit(formDocuments);
    } else if (schemaName === faqSchema) {
      setupFormForEdit(formFAQ);
    } else if (schemaName === sponsorshipSchema) {
      setupFormForEdit(formSponsorship);
    }
  };

  // Cancel editing and reset form
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingSchema("");
    setEditingRecordId("");
    // Reset appropriate form to initial values
    if (editingSchema === historyMilestonesSchema) {
      resetFormHistoryMilestones();
    } else if (editingSchema === membershipTypesSchema) {
      resetFormMembershipTypes();
    } else if (editingSchema === contactLocationsSchema) {
      resetFormContactLocations();
    } else if (editingSchema === hoursstatus) {
      resetFormHoursStatus();
    } else if (editingSchema === photoGallerySchema) {
      resetFormPhotoGallery();
    } else if (editingSchema === addphotogallery) {
      resetFormAddPhotoGallery();
    } else if (editingSchema === committeeLeadershipSchema) {
      resetFormCommitteeLeadership();
    } else if (editingSchema === noticeBoardSchema) {
      resetFormNoticeBoard();
    } else if (editingSchema === bankingSchema) {
      resetFormBanking();
    } else if (editingSchema === documentsSchema) {
      resetFormDocuments();
    } else if (editingSchema === faqSchema) {
      resetFormFAQ();
    } else if (editingSchema === sponsorshipSchema) {
      resetFormSponsorship();
    }
  };

  // Reset the contact locations form to initial state
  const resetFormContactLocations = () => {
    formContactLocations.reset();
    formContactLocations.setInitialValues({});
    formContactLocations.setValues({});
    if (id) {
      formContactLocations.setInitialValues({ jdid: id, is_active: true });
      formContactLocations.setValues({ jdid: id, is_active: true });
    }
    formContactLocations.clearErrors();
    formContactLocations.clearFormGraph();
  };

  // Reset the hours status form to initial state
  const resetFormHoursStatus = () => {
    formHoursStatus.reset();
    formHoursStatus.setInitialValues({});
    formHoursStatus.setValues({});
    if (id) {
      formHoursStatus.setInitialValues({ jdid: id, is_active: true });
      formHoursStatus.setValues({ jdid: id, is_active: true });
    }
    formHoursStatus.clearErrors();
    formHoursStatus.clearFormGraph();
  };

  // Reset the photo gallery form to initial state
  const resetFormPhotoGallery = () => {
    formPhotoGallery.reset();
    formPhotoGallery.setInitialValues({});
    formPhotoGallery.setValues({});
    if (id) {
      formPhotoGallery.setInitialValues({ jdid: id });
      formPhotoGallery.setValues({ jdid: id });
    }
    formPhotoGallery.clearErrors();
    formPhotoGallery.clearFormGraph();
  };

  // Reset the add photo gallery form to initial state
  const resetFormAddPhotoGallery = () => {
    formAddPhotoGallery.reset();
    formAddPhotoGallery.setInitialValues({});
    formAddPhotoGallery.setValues({});
    if (id) {
      formAddPhotoGallery.setInitialValues({ jdid: id });
      formAddPhotoGallery.setValues({ jdid: id });
    }
    formAddPhotoGallery.clearErrors();
    formAddPhotoGallery.clearFormGraph();
  };

  // Reset the committee leadership form to initial state
  const resetFormCommitteeLeadership = () => {
    formCommitteeLeadership.reset();
    formCommitteeLeadership.setInitialValues({});
    formCommitteeLeadership.setValues({});
    if (id) {
      formCommitteeLeadership.setInitialValues({ jdid: id });
      formCommitteeLeadership.setValues({ jdid: id });
    }
    formCommitteeLeadership.clearErrors();
    formCommitteeLeadership.clearFormGraph();
  };

  // Reset the notice board form to initial state
  const resetFormNoticeBoard = () => {
    formNoticeBoard.reset();
    formNoticeBoard.setInitialValues({});
    formNoticeBoard.setValues({});
    if (id) {
      formNoticeBoard.setInitialValues({ jdid: id });
      formNoticeBoard.setValues({ jdid: id });
    }
    formNoticeBoard.clearErrors();
    formNoticeBoard.clearFormGraph();
  };

  // Reset the documents form to initial state
  const resetFormDocuments = () => {
    formDocuments.reset();
    formDocuments.setInitialValues({});
    formDocuments.setValues({});
    if (id) {
      formDocuments.setInitialValues({ jdid: id });
      formDocuments.setValues({ jdid: id });
    }
    formDocuments.clearErrors();
    formDocuments.clearFormGraph();
  };

  // Reset the FAQ form to initial state
  const resetFormFAQ = () => {
    formFAQ.reset();
    formFAQ.setInitialValues({});
    formFAQ.setValues({});
    if (id) {
      formFAQ.setInitialValues({ jdid: id });
      formFAQ.setValues({ jdid: id });
    }
    formFAQ.clearErrors();
    formFAQ.clearFormGraph();
  };

  // Reset the banking form to initial state
  const resetFormBanking = () => {
    formBanking.reset();
    formBanking.setInitialValues({});
    formBanking.setValues({});
    if (id) {
      formBanking.setInitialValues({ jdid: id });
      formBanking.setValues({ jdid: id });
    }
    formBanking.clearErrors();
    formBanking.clearFormGraph();
  };

  // Reset the history milestones form to initial state
  const resetFormHistoryMilestones = () => {
    formHistoryMilestones.reset();
    formHistoryMilestones.setInitialValues({});
    formHistoryMilestones.setValues({});
    if (id) {
      formHistoryMilestones.setInitialValues({ jdid: id, is_active: true });
      formHistoryMilestones.setValues({ jdid: id, is_active: true });
    }
    formHistoryMilestones.clearErrors();
    formHistoryMilestones.clearFormGraph();
  };

  // Reset the membership types form to initial state
  const resetFormMembershipTypes = () => {
    formMembershipTypes.reset();
    formMembershipTypes.setInitialValues({});
    formMembershipTypes.setValues({});
    if (id) {
      formMembershipTypes.setInitialValues({ jdid: id });
      formMembershipTypes.setValues({ jdid: id });
    }
    formMembershipTypes.clearErrors();
    formMembershipTypes.clearFormGraph();
  };

  // Reset the sponsorship form to initial state
  const resetFormSponsorship = () => {
    formSponsorship.reset();
    formSponsorship.setInitialValues({});
    formSponsorship.setValues({});
    if (id) {
      formSponsorship.setInitialValues({ jdid: id });
      formSponsorship.setValues({ jdid: id });
    }
    formSponsorship.clearErrors();
    formSponsorship.clearFormGraph();
  };

  // Reset the overview form to initial state
  // const resetFormOverview = () => {
  //   formOverview.reset();
  //   formOverview.setInitialValues({});
  //   formOverview.setValues({});
  //   if (id) {
  //     formOverview.setInitialValues({ id: id, jdid: id });
  //     formOverview.setValues({ id: id, jdid: id });
  //   }
  //   formOverview.clearErrors();
  //   formOverview.clearFormGraph();
  // };

  const [refreshKey, setRefreshKey] = useState(0);
  const [eventTickets, setEventTickets] = useState<any[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState<boolean>(false);
  const [selectedTicketCategoryFilter, setSelectedTicketCategoryFilter] =
    useState<string | null>(null);
  const [selectedDonationCategoryFilter, setSelectedDonationCategoryFilter] =
    useState<string | null>(null);
  const [headerUpdateKey, setHeaderUpdateKey] = useState(0);

  // Loading states for each form to prevent duplicate submissions
  const [savingTicket, setSavingTicket] = useState(false);
  const [savingVolunteer, setSavingVolunteer] = useState(false);
  const [savingSession, setSavingSession] = useState(false);
  const [savingDonation, setSavingDonation] = useState(false);
  const [savingSponsorship, setSavingSponsorship] = useState(false);
  const [savingCommunication, setSavingCommunication] = useState(false);
  const [savingQRCheckIn, setSavingQRCheckIn] = useState(false);

  // Volunteer signups state
  const [volunteerSignups, setVolunteerSignups] = useState<any[]>([]);
  const [signupsLoading, setSignupsLoading] = useState<boolean>(false);

  // Fetch event data in edit mode for header display
  const { data: eventData } = useQuery({
    queryKey: ["dynamic-model", basicInfoSchema, id],
    queryFn: () =>
      DynamicModuleService.getItemById(basicInfoSchema ?? "", id ?? ""),
    enabled: isEditMode && !!basicInfoSchema && !!id,
  });

  const refreshTableData = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Fetch event tickets when in edit mode
  useEffect(() => {
    const fetchEventTickets = async () => {
      if (!id || !isEditMode) {
        setEventTickets([]);
        return;
      }

      try {
        setTicketsLoading(true);
        const tickets = await EventService.getEventTickets(id, false);
        setEventTickets(tickets || []);
      } catch (error) {
        console.error("Error fetching event tickets:", error);
        setEventTickets([]);
      } finally {
        setTicketsLoading(false);
      }
    };

    fetchEventTickets();
  }, [id, isEditMode, refreshKey]);

  // Fetch volunteer signups when in edit mode
  useEffect(() => {
    const fetchVolunteerSignups = async () => {
      if (!id || !isEditMode) {
        setVolunteerSignups([]);
        return;
      }

      try {
        setSignupsLoading(true);
        const signups = await EventService.getVolunteerSignups(id, false);
        setVolunteerSignups(signups || []);
      } catch (error) {
        console.error("Error fetching volunteer signups:", error);
        setVolunteerSignups([]);
      } finally {
        setSignupsLoading(false);
      }
    };

    fetchVolunteerSignups();
  }, [id, isEditMode, refreshKey]);

  const filteredTickets = useMemo(() => {
    if (!selectedTicketCategoryFilter) {
      return eventTickets;
    }
    const normalizedFilter = selectedTicketCategoryFilter.toLowerCase();
    return eventTickets.filter(record => {
      const categoryLabel = getTicketCategoryLabel(record);
      return (
        typeof categoryLabel === "string" &&
        categoryLabel.toLowerCase() === normalizedFilter
      );
    });
  }, [eventTickets, selectedTicketCategoryFilter]);

  // Handle conditional field visibility based on event price
  useEffect(() => {
    if (formOverview) {
      // Show registration date fields by default
      formOverview.setFieldState("Registrationstartdate", state => {
        if (state) {
          state.visible = true;
        }
      });

      formOverview.setFieldState("registrationenddate", state => {
        if (state) {
          state.visible = true;
        }
      });

      // Set default value for eventprice to "paid"
      formOverview.setFieldState("eventprice", state => {
        if (state) {
          // Set default value to "paid"
          if (!state.value) {
            state.value = "paid";
            formOverview.setValues({
              ...formOverview.values,
              eventprice: "paid",
            });
          }

          const existingOnChange = state.componentProps?.onChange;

          state.componentProps = {
            ...state.componentProps,
            onChange: (value: string) => {
              // Call existing onChange if it exists
              if (existingOnChange) {
                existingOnChange(value);
              }

              // Update state
              setEventPrice(value);

              // Always show registration date fields regardless of paid/free selection
              formOverview.setFieldState("Registrationstartdate", state => {
                if (state) {
                  state.visible = true;
                }
              });

              formOverview.setFieldState("registrationenddate", state => {
                if (state) {
                  state.visible = true;
                }
              });
            },
          };
        }
      });
    }
  }, [formOverview]);

  // Handle initial field visibility when form loads with existing data
  useEffect(() => {
    if (formOverview && isEditMode) {
      // Check if there's an existing eventprice value and set field visibility accordingly
      const currentEventPrice = formOverview.values.eventprice;
      if (currentEventPrice) {
        setEventPrice(currentEventPrice);

        // Always show registration date fields regardless of paid/free selection
        formOverview.setFieldState("Registrationstartdate", state => {
          if (state) {
            state.visible = true;
          }
        });

        formOverview.setFieldState("registrationenddate", state => {
          if (state) {
            state.visible = true;
          }
        });
      } else {
        // If no eventprice value, show fields by default and set to paid
        formOverview.setFieldState("Registrationstartdate", state => {
          if (state) {
            state.visible = true;
          }
        });

        formOverview.setFieldState("registrationenddate", state => {
          if (state) {
            state.visible = true;
          }
        });

        // Set default eventprice to "paid"
        formOverview.setValues({ ...formOverview.values, eventprice: "paid" });
        setEventPrice("paid");
      }
    }
  }, [formOverview, isEditMode]);

  // Watch form value changes and update header when dates are loaded
  useEffect(() => {
    if (isEditMode && formOverview) {
      const values = formOverview.values;
      const start =
        values.eventstartdate ?? values.startdate ?? values.fromdate;
      const end = values.eventenddate ?? values.enddate ?? values.todate;

      // If form values have dates, update the header
      if (start || end) {
        setHeaderUpdateKey(prev => prev + 1);
      }
    }
  }, [
    formOverview?.values?.eventstartdate,
    formOverview?.values?.startdate,
    formOverview?.values?.fromdate,
    formOverview?.values?.eventenddate,
    formOverview?.values?.enddate,
    formOverview?.values?.todate,
    isEditMode,
    formOverview,
  ]);

  // Also update header when eventData is loaded
  useEffect(() => {
    if (isEditMode && eventData) {
      setHeaderUpdateKey(prev => prev + 1);
    }
  }, [eventData, isEditMode]);

  const handleDelete = async (id: string, schemaName: string) => {
    try {
      await DynamicModuleService.deleteItem(schemaName, id);
      refreshTableData();

      // Clear the form if the deleted record was being edited
      if (isEditing && editingSchema === schemaName && editingRecordId === id) {
        handleCancelEdit();
      }

      // Clear the appropriate form based on schema
      if (schemaName === historyMilestonesSchema) {
        resetFormHistoryMilestones();
      } else if (schemaName === membershipTypesSchema) {
        resetFormMembershipTypes();
      } else if (schemaName === contactLocationsSchema) {
        resetFormContactLocations();
      } else if (schemaName === hoursstatus) {
        resetFormHoursStatus();
      } else if (schemaName === photoGallerySchema) {
        resetFormPhotoGallery();
      } else if (schemaName === addphotogallery) {
        resetFormAddPhotoGallery();
      } else if (schemaName === committeeLeadershipSchema) {
        resetFormCommitteeLeadership();
      } else if (schemaName === noticeBoardSchema) {
        resetFormNoticeBoard();
      } else if (schemaName === bankingSchema) {
        resetFormBanking();
      } else if (schemaName === documentsSchema) {
        resetFormDocuments();
      } else if (schemaName === faqSchema) {
        resetFormFAQ();
      } else if (schemaName === sponsorshipSchema) {
        resetFormSponsorship();
      }

      // notify.success("Record deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      notify.error("Failed to delete record");
    }
  };

  /**
   * Custom component for rendering membership types as cards
   */
  const MembershipTypesCard: React.FC<{
    selectedCategory: string | null;
    onCategorySelect: (category: string | null) => void;
  }> = ({ selectedCategory, onCategorySelect }) => {
    const { data: membershipData, isLoading } = useQuery({
      queryKey: ["membershipTypes", membershipTypesSchema, id, refreshKey],
      queryFn: () =>
        DynamicModuleService.getGridCoByConfig(
          membershipTypesSchema || "",
          1,
          100, // Get more records for card view
          [
            "id",
            "category",
            "benefits",
            "price",
            "ticket",
            "soldticket",
            "is_active",
          ], // Include benefits field, soldticket, and is_active
          {
            jdid: id, // Use the chapter ID from URL params for filtering
          }
        ),
      enabled: !!membershipTypesSchema && !!id,
      // Keep previous data to prevent blinking during updates
      placeholderData: previousData => previousData,
      // Use stale time to prevent unnecessary refetches
      staleTime: 1000,
    });

    // State to track selected card
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    // Refs for horizontal scroll control
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // No automatic refresh - updates are handled via optimistic state management
    // This prevents blinking and provides smooth updates

    useEffect(() => {
      if (!selectedCategory) {
        setSelectedCardId(null);
      }
    }, [selectedCategory]);

    // Handle edit ticket with card selection
    const handleEditWithSelection = (ticket: any) => {
      setSelectedCardId(ticket.id);
      onCategorySelect(getTicketCategoryLabel(ticket));
      handleEditTicket(ticket);
    };

    // Scroll functions
    const scrollLeft = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: -400, behavior: "smooth" });
      }
    };

    const scrollRight = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: 400, behavior: "smooth" });
      }
    };

    if (isLoading) {
      return <div>Loading Tickets...</div>;
    }

    // Extract membershipTypes array from membershipData response
    // Handle different response structures: array, {results: []}, {data: []}
    const membershipTypes = (() => {
      if (!membershipData) {
        return [];
      }
      // If it's already an array, return it
      if (Array.isArray(membershipData)) {
        return membershipData;
      }
      // If it has a results property (common pagination pattern)
      if (membershipData.results && Array.isArray(membershipData.results)) {
        return membershipData.results;
      }
      // If it has a data property
      if (membershipData.data && Array.isArray(membershipData.data)) {
        return membershipData.data;
      }
      // If it's an object but not an array, return empty array
      return [];
    })();

    const normalizedSelectedCategory =
      selectedCategory?.toLowerCase?.() ?? null;

    return (
      <div className="mt-6">
        <div className="mb-4 text-xl font-semibold text-foreground">
          Tickets
        </div>

        {membershipTypes.length === 0 ? (
          <div className="py-10 text-center text-base text-muted-foreground">
            No tickets found. Add a new ticket using the form above.
          </div>
        ) : (
          <div className="relative">
            {/* Left Arrow Button */}
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-card p-2 shadow-lg transition-colors hover:bg-muted"
              aria-label="Scroll left"
            >
              <svg
                className="h-6 w-6 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Cards Container with Horizontal Scroll */}
            <div
              ref={scrollContainerRef}
              className="scrollbar-hide flex gap-6 overflow-x-auto px-6 py-4 sm:px-12"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {membershipTypes.map((ticket: any) => {
                const ticketCategoryLabel = getTicketCategoryLabel(ticket);
                // Use numeric parsing and fallbacks for robustness
                const parseNumber = (v: any): number => {
                  const n = Number(v);
                  return Number.isFinite(n) ? n : 0;
                };

                const parseSoldFromAny = (v: any): number => {
                  if (Array.isArray(v) && v.length) {
                    return parseNumber(v[v.length - 1]);
                  }
                  if (typeof v === "string") {
                    const nums = v.match(/\d+/g)?.map(Number) ?? [];
                    return nums.length
                      ? Number.isFinite(nums[nums.length - 1])
                        ? nums[nums.length - 1]
                        : 0
                      : 0;
                  }
                  return parseNumber(v);
                };

                const totalCount = Math.max(0, parseNumber(ticket.ticket));
                let soldCount = parseSoldFromAny(ticket.soldticket);

                if (
                  !Number.isFinite(soldCount) ||
                  soldCount < 0 ||
                  soldCount > totalCount
                ) {
                  // deterministic pseudo-random based on id, capped within [0, totalCount]
                  const idStr = String(ticket.id ?? "");
                  const seed = idStr
                    .split("")
                    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
                  soldCount = totalCount ? seed % (totalCount + 1) : 0;
                }

                const progressPercentage = totalCount
                  ? Math.round((soldCount / totalCount) * 100)
                  : 0;

                // Check if this card is selected - also check if we're currently editing this ticket
                const isCardSelectedById = selectedCardId === ticket.id;
                const isCardSelectedByCategory =
                  normalizedSelectedCategory &&
                  ticketCategoryLabel &&
                  ticketCategoryLabel.toLowerCase() ===
                    normalizedSelectedCategory;
                const isSelected =
                  (isCardSelectedById || isCardSelectedByCategory) ??
                  (isEditing &&
                    editingSchema === membershipTypesSchema &&
                    editingRecordId === ticket.id);

                return (
                  <div
                    key={ticket.id}
                    className={`flex min-h-[280px] w-[85vw] shrink-0 cursor-pointer flex-col gap-4 rounded-2xl p-5 shadow-md transition-all duration-300 sm:w-[380px] ${
                      isSelected
                        ? "border-2 border-primary bg-card"
                        : "border border-primary/50 bg-card"
                    }`}
                    style={{
                      position: "relative",
                    }}
                    onClick={() => {
                      // Check if this card is already selected (by ID or by category)
                      const isAlreadySelected =
                        isCardSelectedById || isCardSelectedByCategory;

                      if (isAlreadySelected) {
                        // If already selected, deselect and clear filter
                        setSelectedCardId(null);
                        onCategorySelect(null);
                      } else {
                        // If not selected, select this card and set filter
                        setSelectedCardId(ticket.id);
                        onCategorySelect(ticketCategoryLabel ?? null);
                      }
                    }}
                  >
                    {/* Header Section */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          flex: 1,
                        }}
                      >
                        <div
                          style={{
                            opacity: 0.8,
                            color: "hsl(var(--muted-foreground))",
                            fontSize: "16px",
                            fontWeight: 500,
                            fontFamily:
                              'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                          }}
                        >
                          {ticketCategoryLabel ?? "Ticket"}
                        </div>
                      </div>

                      {/* Price Badge, Active Toggle, and Edit Button in one line */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {/* Price Badge */}
                        <div className="inline-flex items-center justify-center gap-2.5 rounded-[50px] bg-muted px-3.5 py-1.5">
                          <div className="justify-start text-sm font-semibold capitalize leading-6 tracking-tight text-primary">
                            ${ticket.price ?? "0"}
                          </div>
                        </div>
                        {/* Active Toggle Button */}
                        <div
                          onClick={e => {
                            e.stopPropagation(); // Prevent card selection when clicking toggle
                          }}
                        >
                          <ActiveToggleButton
                            table={membershipTypesSchema}
                            pk={ticket.id}
                            is_active={ticket.is_active}
                          />
                        </div>
                        {/* Edit Button */}
                        <Button
                          type="text"
                          style={{
                            padding: "8px",
                            height: "40px",
                            width: "40px",
                            minWidth: "auto",
                            borderRadius: "50%",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background-color 0.2s ease",
                          }}
                          className={isSelected ? "bg-primary" : "bg-muted"}
                          onClick={e => {
                            e.stopPropagation(); // Prevent card selection when clicking edit button
                            handleEditWithSelection(ticket);
                          }}
                        >
                          <Edit2
                            size={16}
                            className={
                              isSelected
                                ? "text-primary-foreground"
                                : "text-primary"
                            }
                          />
                        </Button>
                      </div>
                    </div>

                    {/* Progress Section */}
                    <div className="flex w-full flex-col items-start justify-start gap-2 self-stretch sm:gap-3">
                      {/* Sold Tickets Text */}
                      <div className="justify-start self-stretch break-words text-base leading-6 text-foreground sm:text-lg sm:leading-7">
                        Sold: {soldCount}/{totalCount}
                      </div>
                      {/* Progress Bar with Gradient */}
                      <div className="relative h-2 w-full self-stretch overflow-hidden rounded-full bg-muted sm:h-2.5">
                        <div
                          className="absolute left-0 top-0 h-full rounded-full"
                          style={{
                            width: `${Math.min(progressPercentage, 100)}%`,
                            background:
                              "linear-gradient(to right, hsl(var(--accent)), hsl(var(--primary)))",
                          }}
                        />
                      </div>
                    </div>

                    {/* Benefits Section */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          color: "hsl(var(--foreground))",
                          fontSize: "16px",
                          fontWeight: 500,
                          fontFamily:
                            'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                        }}
                      >
                        Benefits
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {ticket.benefits ? (
                          Array.isArray(ticket.benefits) ? (
                            ticket.benefits.map((benefit: string) => (
                              <div
                                key={`${ticket.id}-benefit-${benefit.replace(/\s+/g, "-").toLowerCase()}`}
                                style={{
                                  color: "hsl(var(--muted-foreground))",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  fontFamily:
                                    'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                                  lineHeight: "1.4",
                                }}
                              >
                                {benefit}
                              </div>
                            ))
                          ) : (
                            // Split by line breaks and create bullet points for each line
                            ticket.benefits
                              .split("\n")
                              .filter((line: string) => line.trim() !== "")
                              .map((line: string) => (
                                <div
                                  key={`${ticket.id}-benefit-${line.trim().replace(/\s+/g, "-").toLowerCase()}`}
                                  style={{
                                    color: "hsl(var(--muted-foreground))",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    fontFamily:
                                      'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                                    lineHeight: "1.4",
                                  }}
                                >
                                  {line.trim()}
                                </div>
                              ))
                          )
                        ) : (
                          <>
                            <div
                              style={{
                                color: "hsl(var(--muted-foreground))",
                                fontSize: "14px",
                                fontWeight: 500,
                                fontFamily:
                                  'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                                lineHeight: "1.4",
                              }}
                            >
                              Standard event access included
                            </div>
                            <div
                              style={{
                                color: "hsl(var(--muted-foreground))",
                                fontSize: "14px",
                                fontWeight: 500,
                                fontFamily:
                                  'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                                lineHeight: "1.4",
                              }}
                            >
                              Basic amenities provided
                            </div>
                            <div
                              style={{
                                color: "hsl(var(--muted-foreground))",
                                fontSize: "14px",
                                fontWeight: 500,
                                fontFamily:
                                  'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                                lineHeight: "1.4",
                              }}
                            >
                              General seating available
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Arrow Button */}
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-card p-2 shadow-lg transition-colors hover:bg-muted"
              aria-label="Scroll right"
            >
              <svg
                className="h-6 w-6 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  };

  /**
   * Custom component for rendering volunteers as cards
   */
  /**
   * Custom component for rendering volunteers as cards with same design as Tickets
   */

  /**
   * Custom component for rendering agenda & sessions as cards
   */
  /**
   * Custom component for rendering agenda & sessions as cards
   */

  /**
   * Custom component for rendering donation cards
   */
  /**
   * Custom component for rendering donation cards
   */
  const DonationCard: React.FC<{
    selectedCategory: string | null;
    onCategorySelect: (category: string | null) => void;
  }> = ({ selectedCategory, onCategorySelect }) => {
    // Fetch donations from API - only in edit mode, filtered by event_id (jdid)
    const { data: apiDonationsData, isLoading: isLoadingApiDonations } =
      useQuery({
        queryKey: ["event-donations", id, refreshKey],
        queryFn: () => EventService.getEventDonations(id ?? "", false),
        enabled: !!id && isEditMode,
        placeholderData: previousData => previousData,
        staleTime: 1000,
      });

    const { data: donationData, isLoading } = useQuery({
      queryKey: ["donations", hoursstatus, id, refreshKey],
      queryFn: () =>
        DynamicModuleService.getGridCoByConfig(
          hoursstatus || "",
          1,
          100,
          [
            "id",
            "donationtitle",
            "category",
            "donationamountneeded",
            "donationamountraised",
            "descripction",
            "is_active",
            "fromdate",
            "todate",
          ],
          {
            jdid: id,
          }
        ),
      enabled: !!hoursstatus && !!id,
      // Keep previous data to prevent blinking during refetch
      placeholderData: previousData => previousData,
      // Use stale time to prevent unnecessary refetches
      staleTime: 1000,
    });

    // State to track selected card
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    // Refs for horizontal scroll control
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // State to force re-render when form values change during editing
    const [, setFormUpdateKey] = useState(0);

    // No automatic refresh - updates are handled via optimistic state management
    // This prevents blinking and provides smooth updates

    // Watch form values when editing to trigger re-renders
    useEffect(() => {
      if (isEditing && editingSchema === hoursstatus) {
        const subscriptionId = formHoursStatus.subscribe(({ type }) => {
          if (
            type === "onFieldValueChange" ||
            type === "onFieldInputValueChange"
          ) {
            setFormUpdateKey(prev => prev + 1);
          }
        });
        return () => formHoursStatus.unsubscribe(subscriptionId);
      }
      // isEditing and editingSchema are from parent component state
      // formHoursStatus is a stable form object reference
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing, editingSchema]);

    useEffect(() => {
      if (!selectedCategory) {
        setSelectedCardId(null);
      }
    }, [selectedCategory]);

    // Handle edit donation with card selection
    const handleEditWithSelection = (donation: any) => {
      setSelectedCardId(donation.id);
      onCategorySelect(getDonationCategoryLabel(donation));
      handleEdit(donation, hoursstatus);
    };

    // Scroll functions
    const scrollLeft = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: -400, behavior: "smooth" });
      }
    };

    const scrollRight = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: 400, behavior: "smooth" });
      }
    };

    // Extract donations array from response (handle both array and object with results)
    const donations = useMemo(() => {
      if (!donationData) {
        return [];
      }
      if (Array.isArray(donationData)) {
        return donationData;
      }
      if (
        typeof donationData === "object" &&
        donationData !== null &&
        "results" in donationData &&
        Array.isArray((donationData as any).results)
      ) {
        return (donationData as any).results;
      }
      return [];
    }, [donationData]);

    // Wrap apiDonations in useMemo to ensure stable reference for filteredApiDonations
    const apiDonations = useMemo(() => {
      if (!apiDonationsData) {
        return [];
      }
      if (Array.isArray(apiDonationsData)) {
        return apiDonationsData;
      }
      if (
        typeof apiDonationsData === "object" &&
        apiDonationsData !== null &&
        "results" in apiDonationsData &&
        Array.isArray((apiDonationsData as any).results)
      ) {
        return (apiDonationsData as any).results;
      }
      return [];
    }, [apiDonationsData]);

    // Filter donations by event_id (jdid) to ensure only donations for current event are shown
    // This must be called before any early returns to follow Rules of Hooks
    const filteredApiDonations = useMemo(() => {
      if (!id) {
        return [];
      }
      let filtered = apiDonations.filter(
        (donation: any) => donation.event_id === id
      );

      // Apply category filter if selected
      if (selectedCategory) {
        const normalizedFilter = selectedCategory.toLowerCase();
        filtered = filtered.filter((donation: any) => {
          const categoryLabel = getDonationCategoryLabel(donation);
          return (
            typeof categoryLabel === "string" &&
            categoryLabel.toLowerCase() === normalizedFilter
          );
        });
      }

      return filtered;
      // id is from useParams in parent component - component re-renders when it changes
    }, [apiDonations, selectedCategory]);

    // Early return must come AFTER all hooks
    if (isLoading || isLoadingApiDonations) {
      return <div>Loading Donations...</div>;
    }

    // Debug: Log the API donations data
    console.log("API Donations Data:", apiDonations);
    console.log("Filtered Donations by event_id:", filteredApiDonations);

    // Map API donations to table format with status directly from API
    const donationTableData = filteredApiDonations.map(
      (donation: any, index: number) => ({
        key: donation.id ?? index,
        srNo: index + 1,
        id: donation.id,
        name: donation.name ?? "-",
        email: donation.email ?? "-",
        phone: donation.phone ?? "-",
        category: donation.category ?? "-",
        donationTitle: donation.donation_title ?? donation.donationtitle ?? "-",
        donationAmount: donation.donation_amount
          ? `$${parseFloat(donation.donation_amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "-",
        message: donation.message ?? "-",
        status: donation.status ?? "-",
      })
    );

    // Debug: Log the mapped table data
    console.log("Donation Table Data:", donationTableData);

    return (
      <div className="mt-6">
        <div className="mb-4 text-xl font-semibold text-foreground">
          Donations
        </div>

        {donations.length === 0 ? (
          <div className="py-10 text-center text-base text-muted-foreground">
            No donations found. Add a new donation using the form above.
          </div>
        ) : (
          <div className="relative">
            {/* Left Arrow Button */}
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-card p-2 shadow-lg transition-colors hover:bg-muted"
              aria-label="Scroll left"
            >
              <svg
                className="h-6 w-6 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Cards Container with Horizontal Scroll */}
            <div
              ref={scrollContainerRef}
              className="scrollbar-hide flex gap-6 overflow-x-auto px-6 py-4 sm:px-12"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {donations.map((donation: any) => {
                const donationCategoryLabel =
                  getDonationCategoryLabel(donation);
                // Check if this card is selected
                const isCardSelectedById = selectedCardId === donation.id;
                const isCardSelectedByCategory =
                  selectedCategory &&
                  donationCategoryLabel &&
                  donationCategoryLabel.toLowerCase() ===
                    selectedCategory.toLowerCase();
                const isSelected =
                  (isCardSelectedById || isCardSelectedByCategory) ??
                  (isEditing &&
                    editingSchema === hoursstatus &&
                    editingRecordId === donation.id);

                // Use original donation data - don't merge form values until saved
                // This prevents the card from updating while user is typing
                const donationDataToUse = donation;

                // Compute progress from donationamountraised/donationamountneeded with safe parsing
                const parseNumber = (v: any): number => {
                  if (v === null || v === undefined || v === "") {
                    return 0;
                  }
                  const n = Number(v);
                  return Number.isFinite(n) ? n : 0;
                };
                const parseFromAny = (v: any): number => {
                  if (v === null || v === undefined || v === "") {
                    return 0;
                  }
                  if (Array.isArray(v) && v.length) {
                    return parseNumber(v[v.length - 1]);
                  }
                  if (typeof v === "string") {
                    // Try to parse as decimal number first (handles "300.0", "300.5", etc.)
                    const parsed = parseFloat(v.trim());
                    if (Number.isFinite(parsed)) {
                      return parsed;
                    }
                    // Fallback: extract numbers if it's not a simple number string
                    const nums = v.match(/\d+\.?\d*/g)?.map(Number) ?? [];
                    return nums.length
                      ? Number.isFinite(nums[nums.length - 1])
                        ? nums[nums.length - 1]
                        : 0
                      : 0;
                  }
                  return parseNumber(v);
                };

                const targetAmount = Math.max(
                  0,
                  parseNumber(donationDataToUse.donationamountneeded)
                );
                let raisedAmount = parseFromAny(
                  donationDataToUse.donationamountraised
                );
                // Only set to 0 if not a valid number or negative
                // Allow raisedAmount to exceed targetAmount
                if (!Number.isFinite(raisedAmount) || raisedAmount < 0) {
                  raisedAmount = 0;
                }
                const progressPercentage = targetAmount
                  ? Math.round((raisedAmount / targetAmount) * 100)
                  : 0;

                // Calculate days left from todate
                const daysLeft = calculateDaysLeft(donationDataToUse.todate);

                // Calculate status based on percentage
                const getDonationStatus = (percentage: number): string => {
                  if (percentage >= 100) {
                    return "Goal Reached";
                  } else if (percentage > 50 && percentage < 100) {
                    return "On Track";
                  } else if (percentage < 30) {
                    return "Low Response";
                  } else {
                    // For 30% to 50% range, default to "On Track"
                    return "On Track";
                  }
                };

                const donationStatus = getDonationStatus(progressPercentage);

                return (
                  <div
                    key={donation.id}
                    className={`flex w-[85vw] shrink-0 cursor-pointer flex-col gap-4 rounded-2xl p-5 shadow-md transition-all duration-300 sm:w-[420px] ${
                      isSelected
                        ? "border-2 border-primary bg-card"
                        : "border border-primary/30 bg-card"
                    }`}
                    style={{ position: "relative" }}
                    onClick={() => {
                      // Check if this card is already selected (by ID or by category)
                      const isAlreadySelected =
                        isCardSelectedById || isCardSelectedByCategory;

                      if (isAlreadySelected) {
                        // If already selected, deselect and clear filter
                        setSelectedCardId(null);
                        onCategorySelect(null);
                      } else {
                        // If not selected, select this card and set filter
                        setSelectedCardId(donation.id);
                        onCategorySelect(donationCategoryLabel ?? null);
                      }
                    }}
                  >
                    {/* Header Section */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        width: "100%",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          flex: 1,
                          minWidth: 0, // Allow flex item to shrink below content size
                        }}
                      >
                        <div
                          style={{
                            opacity: 0.8,
                            color: "hsl(var(--foreground))",
                            fontSize: "18px",
                            fontWeight: 600,
                            fontFamily:
                              'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                            lineHeight: "24px",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            hyphens: "auto",
                          }}
                        >
                          {donationDataToUse.donationtitle ?? "Donation"}
                        </div>
                      </div>

                      {/* Category Badge, Active Toggle, and Edit Button in one line */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexShrink: 0, // Prevent shrinking
                        }}
                      >
                        {/* Category Badge */}
                        <div className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-[50px] bg-muted px-3.5 py-1.5">
                          <div className="justify-start whitespace-nowrap text-sm font-semibold capitalize leading-6 tracking-tight text-primary">
                            {donationDataToUse.category ?? "Category"}
                          </div>
                        </div>
                        {/* Active Toggle Button */}
                        <div
                          onClick={e => {
                            e.stopPropagation(); // Prevent card selection when clicking toggle
                          }}
                          style={{ flexShrink: 0 }}
                        >
                          <ActiveToggleButton
                            table={hoursstatus}
                            pk={donation.id}
                            is_active={donation.is_active}
                          />
                        </div>
                        {/* Edit Button */}
                        <Button
                          type="text"
                          style={{
                            padding: "8px",
                            height: "40px",
                            width: "40px",
                            minWidth: "40px",
                            borderRadius: "50%",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background-color 0.2s ease",
                            flexShrink: 0,
                          }}
                          className={isSelected ? "bg-primary" : "bg-muted"}
                          onClick={e => {
                            e.stopPropagation();
                            handleEditWithSelection(donation);
                          }}
                        >
                          <Edit2
                            size={16}
                            className={
                              isSelected
                                ? "text-primary-foreground"
                                : "text-primary"
                            }
                          />
                        </Button>
                      </div>
                    </div>

                    {/* Progress Section */}
                    <div className="flex w-full flex-col items-start justify-start gap-2 self-stretch sm:gap-3">
                      {/* Donated Amount Text */}
                      <div className="justify-start self-stretch break-words text-base leading-6 text-foreground sm:text-lg sm:leading-7">
                        Donated: ${raisedAmount.toLocaleString()} of $
                        {targetAmount.toLocaleString()}
                      </div>
                      {/* Progress Bar with Gradient */}
                      <div className="relative h-2 w-full self-stretch overflow-hidden rounded-full bg-muted sm:h-2.5">
                        <div
                          className="absolute left-0 top-0 h-full rounded-full"
                          style={{
                            width: `${Math.min(progressPercentage, 100)}%`,
                            background:
                              "linear-gradient(to right, hsl(var(--accent)), hsl(var(--primary)))",
                          }}
                        />
                      </div>
                    </div>

                    {/* Donation Metrics Card - Funded Percentage and Days Left */}
                    <div className="flex w-full items-center justify-start gap-3 self-stretch rounded-lg bg-muted px-3 py-2 sm:gap-4 sm:px-4 sm:py-2.5">
                      {/* Funded Metric */}
                      <div className="flex flex-1 flex-col items-center justify-center gap-0.5">
                        <div className="justify-start text-center text-lg font-bold leading-5 tracking-tight text-primary sm:text-xl sm:leading-6">
                          {progressPercentage}%
                        </div>
                        <div className="justify-start text-center text-[10px] font-medium capitalize leading-3 text-muted-foreground sm:text-xs sm:leading-4">
                          Funded
                        </div>
                      </div>

                      {/* Days Left Metric */}
                      <div className="flex flex-1 flex-col items-center justify-center gap-0.5">
                        <div className="justify-start text-center text-lg font-bold leading-5 tracking-tight text-primary sm:text-xl sm:leading-6">
                          {daysLeft}
                        </div>
                        <div className="justify-start text-center text-[10px] font-medium capitalize leading-3 text-muted-foreground sm:text-xs sm:leading-4">
                          Days Left
                        </div>
                      </div>
                    </div>
                    {/* Status Badge */}
                    <div className="flex w-full items-center justify-center">
                      {donationStatus === "Goal Reached" ? (
                        <div className="flex w-full items-center justify-center gap-2.5 rounded-[500px] bg-blue-100 px-3 py-1.5">
                          <div className="text-xs font-semibold leading-6 text-blue-800 opacity-80">
                            Goal Reached
                          </div>
                        </div>
                      ) : donationStatus === "On Track" ? (
                        <div className="flex w-full items-center justify-center gap-2.5 rounded-[500px] bg-green-100 px-3 py-1.5">
                          <div className="text-xs font-semibold leading-6 text-lime-800 opacity-80">
                            On Track
                          </div>
                        </div>
                      ) : donationStatus === "Low Response" ? (
                        <div className="flex w-full items-center justify-center gap-2.5 rounded-[500px] bg-red-100 px-3 py-1.5">
                          <div className="text-xs font-semibold leading-6 text-red-800 opacity-80">
                            Low Response
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Arrow Button */}
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-card p-2 shadow-lg transition-colors hover:bg-muted"
              aria-label="Scroll right"
            >
              <svg
                className="h-6 w-6 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Donation Table - Same structure as Tickets tab */}
        <div style={{ marginTop: 50, width: "100%" }}>
          <div className="flex w-full flex-col items-start justify-start gap-3 overflow-x-auto rounded-2xl bg-card p-5">
            <div className="flex w-full min-w-[1340px] flex-col items-start justify-start gap-px">
              {/* Header Row */}
              <div className="flex w-full items-center justify-start">
                <div className="flex h-11 min-w-[80px] flex-[0.6] items-center justify-center gap-2.5 rounded-bl-md rounded-tl-md bg-muted px-3.5 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    SR NO
                  </div>
                </div>
                <div className="flex h-11 min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    DONOR NAME
                  </div>
                </div>
                <div className="flex h-11 min-w-[200px] flex-[1.5] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    EMAIL
                  </div>
                </div>
                <div className="flex h-11 min-w-[140px] flex-[1] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    PHONE
                  </div>
                </div>
                {!selectedCategory && (
                  <div className="flex h-11 min-w-[140px] flex-[1] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                    <div
                      className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                      style={{
                        textAlign: "center",
                      }}
                    >
                      CATEGORY
                    </div>
                  </div>
                )}
                <div className="flex h-11 min-w-[200px] flex-[1.5] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    DONATION TITLE
                  </div>
                </div>
                <div className="flex h-11 min-w-[140px] flex-[1] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    AMOUNT (USD)
                  </div>
                </div>
                <div className="flex h-11 min-w-[120px] flex-[0.8] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    STATUS
                  </div>
                </div>
                <div className="flex h-11 min-w-[100px] flex-[0.6] items-center justify-center gap-2.5 rounded-br-md rounded-tr-md bg-muted px-3.5 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    RECEIPT
                  </div>
                </div>
              </div>

              {/* Data Rows */}
              {donationTableData.length === 0 ? (
                <div className="flex w-full items-center justify-center py-8">
                  <div className="text-base font-medium text-gray-500">
                    {selectedCategory
                      ? "No donations found for the selected category."
                      : "No donations found."}
                  </div>
                </div>
              ) : (
                donationTableData.map((record: any) => {
                  const getStatusBadge = (status: string) => {
                    if (status === "-" || !status) {
                      return (
                        <div
                          className="text-sm font-medium leading-6 text-gray-500"
                          style={{
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          -
                        </div>
                      );
                    }
                    if (status === "Goal Reached") {
                      return (
                        <div className="flex items-center justify-center gap-2.5 rounded-[500px] bg-blue-100 px-3 py-1.5">
                          <div className="text-xs font-semibold leading-6 text-blue-800 opacity-80">
                            Goal Reached
                          </div>
                        </div>
                      );
                    } else if (status === "On Track") {
                      return (
                        <div className="flex items-center justify-center gap-2.5 rounded-[500px] bg-green-100 px-3 py-1.5">
                          <div className="text-xs font-semibold leading-6 text-lime-800 opacity-80">
                            On Track
                          </div>
                        </div>
                      );
                    } else if (status === "Low Response") {
                      return (
                        <div className="flex items-center justify-center gap-2.5 rounded-[500px] bg-red-100 px-3 py-1.5">
                          <div className="text-xs font-semibold leading-6 text-red-800 opacity-80">
                            Low Response
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="flex items-center justify-center gap-2.5 rounded-[500px] bg-gray-100 px-3 py-1.5">
                          <div className="text-xs font-semibold leading-6 text-muted-foreground opacity-80">
                            {status}
                          </div>
                        </div>
                      );
                    }
                  };

                  return (
                    <div
                      key={record.key}
                      className="flex w-full items-start justify-start border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex min-h-[60px] min-w-[80px] flex-[0.6] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                        <div
                          className="text-sm font-medium leading-6 text-gray-500"
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          {record.srNo}
                        </div>
                      </div>
                      <div className="flex min-h-[60px] min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                        <div
                          className="text-sm font-medium leading-6 text-gray-500"
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          {record.name}
                        </div>
                      </div>
                      <div className="flex min-h-[60px] min-w-[200px] flex-[1.5] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                        <div
                          className="text-sm font-medium leading-6 text-gray-500"
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          {record.email}
                        </div>
                      </div>
                      <div className="flex min-h-[60px] min-w-[140px] flex-[1] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                        <div
                          className="text-sm font-medium leading-6 text-gray-500"
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          {record.phone}
                        </div>
                      </div>
                      {!selectedCategory && (
                        <div className="flex min-h-[60px] min-w-[140px] flex-[1] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                          <div
                            className="text-sm font-medium leading-6 text-gray-500"
                            style={{
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              textAlign: "center",
                              width: "100%",
                            }}
                          >
                            {record.category}
                          </div>
                        </div>
                      )}
                      <div className="flex min-h-[60px] min-w-[200px] flex-[1.5] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                        <div
                          className="text-sm font-medium leading-6 text-gray-500"
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          {record.donationTitle}
                        </div>
                      </div>
                      <div className="flex min-h-[60px] min-w-[140px] flex-[1] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                        <div
                          className="text-sm font-medium leading-6 text-gray-500"
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          {record.donationAmount}
                        </div>
                      </div>
                      <div className="flex min-h-[60px] min-w-[120px] flex-[0.8] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                        {getStatusBadge(record.status)}
                      </div>
                      <div className="flex min-h-[60px] min-w-[100px] flex-[0.6] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                        <button
                          className="inline-flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-[52.50px] bg-muted transition-colors hover:bg-violet-200"
                          onClick={() => {
                            // Handle view action - you can add navigation or modal here
                            console.log("View donation:", record.id);
                          }}
                        >
                          <img
                            src={eyeIcon}
                            alt="View"
                            className="h-7 w-7 object-contain"
                          />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SponsorshipCard: React.FC = () => {
    const { data: sponsorshipData, isLoading } = useQuery({
      queryKey: ["sponsorships", photoGallerySchema, id, refreshKey],
      queryFn: () =>
        DynamicModuleService.getGridCoByConfig(
          photoGallerySchema || "",
          1,
          100,
          [
            "id",
            "sponsororganization",
            "packagee",
            "amount",
            "benefits",
            "is_active",
          ],
          {
            jdid: id,
          }
        ),
      enabled: !!photoGallerySchema && !!id,
      placeholderData: previousData => previousData,
      staleTime: 1000,
    });

    const { data: sponsorsListData, isLoading: isLoadingSponsorsList } =
      useQuery({
        queryKey: ["event-sponsors-list", id, refreshKey],
        queryFn: () => EventService.getEventSponsorsList(id ?? "", false),
        enabled: !!id,
        placeholderData: previousData => previousData,
        staleTime: 1000,
      });

    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const sponsorships = useMemo(() => {
      if (!sponsorshipData) {
        return [];
      }
      if (Array.isArray(sponsorshipData)) {
        return sponsorshipData;
      }
      if (
        typeof sponsorshipData === "object" &&
        sponsorshipData !== null &&
        "results" in sponsorshipData &&
        Array.isArray((sponsorshipData as any).results)
      ) {
        return (sponsorshipData as any).results;
      }
      return [];
    }, [sponsorshipData]);

    const scrollLeft = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: -400, behavior: "smooth" });
      }
    };

    const scrollRight = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: 400, behavior: "smooth" });
      }
    };

    const formatAmount = (v: unknown): string => {
      if (v === null || v === undefined || v === "") {
        return "—";
      }
      const n = Number(v);
      if (Number.isFinite(n)) {
        return `$${n.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      }
      return String(v);
    };

    const displayAmount = (record: any) =>
      formatAmount(
        record?.amount ?? record?.sponsoramount ?? record?.sponsor_amount
      );

    const handleEditSponsorship = (record: any) => {
      setSelectedCardId(record.id);
      handleEdit(record, photoGallerySchema);
    };

    const sponsorsTableRows = useMemo(() => {
      if (!sponsorsListData) {
        return [];
      }
      if (Array.isArray(sponsorsListData)) {
        return sponsorsListData;
      }
      return [];
    }, [sponsorsListData]);

    const renderSponsorStatusBadge = (status: string) => {
      const s = (status ?? "").trim();
      if (!s || s === "-") {
        return (
          <div
            className="text-sm font-medium leading-6 text-gray-500"
            style={{ textAlign: "center", width: "100%" }}
          >
            —
          </div>
        );
      }
      const lower = s.toLowerCase();
      if (lower === "paid") {
        return (
          <div className="flex items-center justify-center gap-2.5 rounded-[500px] bg-green-100 px-3 py-1.5">
            <div className="text-xs font-semibold leading-6 text-green-800 opacity-90">
              Paid
            </div>
          </div>
        );
      }
      if (lower === "pending" || lower === "unpaid") {
        return (
          <div className="flex items-center justify-center gap-2.5 rounded-[500px] bg-amber-100 px-3 py-1.5">
            <div className="text-xs font-semibold leading-6 text-amber-900 opacity-90">
              {s}
            </div>
          </div>
        );
      }
      return (
        <div className="flex items-center justify-center gap-2.5 rounded-[500px] bg-gray-100 px-3 py-1.5">
          <div className="text-xs font-semibold leading-6 text-muted-foreground opacity-80">
            {s}
          </div>
        </div>
      );
    };

    return (
      <div className="mt-6">
        <div className="mb-4 text-xl font-semibold text-foreground">
          Sponsorships
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-base text-muted-foreground">
            Loading Sponsorships...
          </div>
        ) : sponsorships.length === 0 ? (
          <div className="py-10 text-center text-base text-muted-foreground">
            No sponsorships found. Add a new sponsorship using the form above.
          </div>
        ) : (
          <div className="relative">
            <button
              type="button"
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-card p-2 shadow-lg transition-colors hover:bg-muted"
              aria-label="Scroll left"
            >
              <svg
                className="h-6 w-6 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <div
              ref={scrollContainerRef}
              className="scrollbar-hide flex gap-6 overflow-x-auto px-6 py-4 sm:px-12"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {sponsorships.map((sp: any) => {
                const isCardSelected = selectedCardId === sp.id;
                const isEditingThis =
                  isEditing &&
                  editingSchema === photoGallerySchema &&
                  editingRecordId === sp.id;
                const isSelected = isCardSelected || isEditingThis;

                return (
                  <div
                    key={sp.id}
                    className={`flex w-[85vw] shrink-0 cursor-pointer flex-col gap-4 rounded-2xl p-5 shadow-md transition-all duration-300 sm:w-[420px] ${
                      isSelected
                        ? "border-2 border-primary bg-card"
                        : "border border-primary/30 bg-card"
                    }`}
                    style={{ position: "relative" }}
                    onClick={() => {
                      if (isCardSelected) {
                        setSelectedCardId(null);
                      } else {
                        setSelectedCardId(sp.id);
                      }
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        width: "100%",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            opacity: 0.8,
                            color: "hsl(var(--foreground))",
                            fontSize: "18px",
                            fontWeight: 600,
                            fontFamily:
                              'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                            lineHeight: "24px",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            hyphens: "auto",
                          }}
                        >
                          {String(sp.sponsororganization ?? "").trim() ||
                            "Sponsor"}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexShrink: 0,
                        }}
                      >
                        <div className="inline-flex max-w-[140px] flex-shrink-0 items-center justify-center gap-2.5 rounded-[50px] bg-muted px-3.5 py-1.5">
                          <div className="line-clamp-2 text-sm font-semibold capitalize leading-6 tracking-tight text-primary">
                            {String(sp.packagee ?? "").trim() || "Package"}
                          </div>
                        </div>
                        <div
                          onClick={e => {
                            e.stopPropagation();
                          }}
                          style={{ flexShrink: 0 }}
                        >
                          <ActiveToggleButton
                            table={photoGallerySchema}
                            pk={sp.id}
                            is_active={Boolean(sp.is_active)}
                          />
                        </div>
                        <Button
                          type="text"
                          style={{
                            padding: "8px",
                            height: "40px",
                            width: "40px",
                            minWidth: "40px",
                            borderRadius: "50%",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background-color 0.2s ease",
                            flexShrink: 0,
                          }}
                          className={isSelected ? "bg-primary" : "bg-muted"}
                          onClick={e => {
                            e.stopPropagation();
                            handleEditSponsorship(sp);
                          }}
                        >
                          <Edit2
                            size={16}
                            className={
                              isSelected
                                ? "text-primary-foreground"
                                : "text-primary"
                            }
                          />
                        </Button>
                        <Popconfirm
                          title="Are you sure you want to delete this sponsorship?"
                          onConfirm={() =>
                            handleDelete(sp.id, photoGallerySchema)
                          }
                          okText="Yes"
                          cancelText="No"
                          disabled={
                            isEditing && editingSchema !== photoGallerySchema
                          }
                        >
                          <span
                            onClick={e => e.stopPropagation()}
                            style={{ flexShrink: 0 }}
                          >
                            <CustomButton
                              danger
                              icon={<Trash size={16} />}
                              type="text"
                              disabled={
                                isEditing &&
                                editingSchema !== photoGallerySchema
                              }
                            />
                          </span>
                        </Popconfirm>
                      </div>
                    </div>

                    <div className="flex w-full flex-col gap-2">
                      <div className="text-base font-medium text-foreground sm:text-lg">
                        Amount:{" "}
                        <span className="text-primary">
                          {displayAmount(sp)}
                        </span>
                      </div>
                      <div className="rounded-lg bg-muted px-3 py-2 sm:px-4 sm:py-3">
                        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:text-xs">
                          Benefits
                        </div>
                        <div
                          className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground sm:text-base"
                          style={{ wordBreak: "break-word" }}
                        >
                          {String(sp.benefits ?? "").trim() || "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={scrollRight}
              className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-card p-2 shadow-lg transition-colors hover:bg-muted"
              aria-label="Scroll right"
            >
              <svg
                className="h-6 w-6 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Sponsor payments grid — same style as Donation tab */}
        <div style={{ marginTop: 50, width: "100%" }}>
          <div className="flex w-full flex-col items-start justify-start gap-3 overflow-x-auto rounded-2xl bg-card p-5">
            <div className="flex w-full min-w-[1180px] flex-col items-start justify-start gap-px">
              <div className="flex w-full items-center justify-start">
                <div className="flex h-11 min-w-[72px] flex-[0.5] items-center justify-center gap-2.5 rounded-bl-md rounded-tl-md bg-muted px-3 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{ textAlign: "center" }}
                  >
                    SR NO
                  </div>
                </div>
                <div className="flex h-11 min-w-[120px] flex-[1] items-center justify-center gap-2.5 bg-muted px-3 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{ textAlign: "center" }}
                  >
                    NAME
                  </div>
                </div>
                <div className="flex h-11 min-w-[120px] flex-[1] items-center justify-center gap-2.5 bg-muted px-3 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{ textAlign: "center" }}
                  >
                    PHONE
                  </div>
                </div>
                <div className="flex h-11 min-w-[130px] flex-[1] items-center justify-center gap-2.5 bg-muted px-3 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{ textAlign: "center" }}
                  >
                    SPONSOR AMOUNT
                  </div>
                </div>
                <div className="flex h-11 min-w-[100px] flex-[0.6] items-center justify-center gap-2.5 bg-muted px-3 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{ textAlign: "center" }}
                  >
                    CATEGORY
                  </div>
                </div>
                <div className="flex h-11 min-w-[140px] flex-[1.1] items-center justify-center gap-2.5 bg-muted px-3 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{ textAlign: "center" }}
                  >
                    SPONSOR TITLE
                  </div>
                </div>
                <div className="flex h-11 min-w-[120px] flex-[1] items-center justify-center gap-2.5 bg-muted px-3 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{ textAlign: "center" }}
                  >
                    SPONSOR DATE
                  </div>
                </div>
                <div className="flex h-11 min-w-[110px] flex-[0.7] items-center justify-center gap-2.5 rounded-br-md rounded-tr-md bg-muted px-3 py-2">
                  <div
                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                    style={{ textAlign: "center" }}
                  >
                    STATUS
                  </div>
                </div>
              </div>

              {isLoadingSponsorsList ? (
                <div className="flex w-full items-center justify-center py-8">
                  <div className="text-base font-medium text-gray-500">
                    Loading sponsor records...
                  </div>
                </div>
              ) : sponsorsTableRows.length === 0 ? (
                <div className="flex w-full items-center justify-center py-8">
                  <div className="text-base font-medium text-gray-500">
                    No sponsor records found.
                  </div>
                </div>
              ) : (
                sponsorsTableRows.map((row: any, index: number) => (
                  <div
                    key={row.sponsor_id ?? row.id ?? `sponsor-${index}`}
                    className="flex w-full items-start justify-start border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex min-h-[60px] min-w-[72px] flex-[0.5] items-center justify-center gap-2.5 self-stretch bg-white px-3 py-3">
                      <div
                        className="text-sm font-medium leading-6 text-gray-500"
                        style={{
                          wordBreak: "break-word",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex min-h-[60px] min-w-[120px] flex-[1] items-center justify-center gap-2.5 self-stretch bg-white px-3 py-3">
                      <div
                        className="text-sm font-medium leading-6 text-gray-500"
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {row.name ?? "—"}
                      </div>
                    </div>
                    <div className="flex min-h-[60px] min-w-[120px] flex-[1] items-center justify-center gap-2.5 self-stretch bg-white px-3 py-3">
                      <div
                        className="text-sm font-medium leading-6 text-gray-500"
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {row.phone ?? "—"}
                      </div>
                    </div>
                    <div className="flex min-h-[60px] min-w-[130px] flex-[1] items-center justify-center gap-2.5 self-stretch bg-white px-3 py-3">
                      <div
                        className="text-sm font-medium leading-6 text-gray-500"
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {formatAmount(row.sponsor_amount)}
                      </div>
                    </div>
                    <div className="flex min-h-[60px] min-w-[100px] flex-[0.6] items-center justify-center gap-2.5 self-stretch bg-white px-3 py-3">
                      <div
                        className="text-sm font-medium leading-6 text-gray-500"
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {row.category ?? "—"}
                      </div>
                    </div>
                    <div className="flex min-h-[60px] min-w-[140px] flex-[1.1] items-center justify-center gap-2.5 self-stretch bg-white px-3 py-3">
                      <div
                        className="text-sm font-medium leading-6 text-gray-500"
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {row.sponsor_title ?? "—"}
                      </div>
                    </div>
                    <div className="flex min-h-[60px] min-w-[120px] flex-[1] items-center justify-center gap-2.5 self-stretch bg-white px-3 py-3">
                      <div
                        className="text-sm font-medium leading-6 text-gray-500"
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {row.sponsor_date ?? "—"}
                      </div>
                    </div>
                    <div className="flex min-h-[60px] min-w-[110px] flex-[0.7] items-center justify-center gap-2.5 self-stretch bg-white px-3 py-3">
                      {renderSponsorStatusBadge(
                        typeof row.status === "string" ? row.status : ""
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AgendaSessionsCard: React.FC = () => {
    const { data: sessionsData, isLoading } = useQuery({
      queryKey: ["sessions", contactLocationsSchema, id, refreshKey],
      queryFn: () =>
        DynamicModuleService.getGridCoByConfig(
          contactLocationsSchema || "",
          1,
          100,
          [
            "id",
            "activityname",
            "starttime",
            "endtime",
            "date",
            "speakers",
            "venue",
            "description",
            "is_active",
          ],
          {
            jdid: id,
          }
        ),
      enabled: !!contactLocationsSchema && !!id,
      // Keep previous data to prevent blinking during updates
      placeholderData: previousData => previousData,
      // Use stale time to prevent unnecessary refetches
      staleTime: 1000,
    });

    // State to track selected card
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

    // No automatic refresh - updates are handled via optimistic state management
    // This prevents blinking and provides smooth updates

    // Handle edit session with card selection
    const handleEditWithSelection = (session: any) => {
      setSelectedCardId(session.id);
      handleEdit(session, contactLocationsSchema);
    };

    if (isLoading) {
      return <div>Loading Sessions...</div>;
    }

    // Extract sessions array from sessionsData response
    // Handle different response structures: array, {results: []}, {data: []}
    const sessions = (() => {
      if (!sessionsData) {
        return [];
      }
      // If it's already an array, return it
      if (Array.isArray(sessionsData)) {
        return sessionsData;
      }
      // If it has a results property (common pagination pattern)
      if (sessionsData.results && Array.isArray(sessionsData.results)) {
        return sessionsData.results;
      }
      // If it has a data property
      if (sessionsData.data && Array.isArray(sessionsData.data)) {
        return sessionsData.data;
      }
      // If it's an object but not an array, return empty array
      return [];
    })();

    return (
      <div className="mt-6">
        <div className="mb-4 text-xl font-semibold text-foreground">
          Agenda & Sessions
        </div>

        {sessions.length === 0 ? (
          <div className="py-10 text-center text-base text-muted-foreground">
            No sessions found. Add a new session using the form above.
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "24px",
              padding: "0",
            }}
          >
            {sessions.map((session: any) => {
              // Check if this card is selected
              const isSelected =
                selectedCardId === session.id ||
                (isEditing &&
                  editingSchema === contactLocationsSchema &&
                  editingRecordId === session.id);

              return (
                <div
                  key={session.id}
                  className={`flex min-h-[280px] w-full shrink-0 cursor-pointer flex-col gap-4 rounded-2xl p-5 shadow-md transition-all duration-300 md:w-[calc(50%-12px)] lg:w-[380px] ${
                    isSelected
                      ? "border-2 border-primary bg-card"
                      : "border border-primary/50 bg-card"
                  }`}
                  style={{ position: "relative" }}
                  onClick={() => {
                    if (selectedCardId === session.id) {
                      setSelectedCardId(null);
                    } else {
                      setSelectedCardId(session.id);
                    }
                  }}
                >
                  {/* Header Section */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        flex: 1,
                      }}
                    >
                      <div
                        style={{
                          opacity: 0.8,
                          color: "hsl(var(--muted-foreground))",
                          fontSize: "16px",
                          fontWeight: 500,
                          fontFamily:
                            'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                        }}
                      >
                        {session.activityname ?? "Session"}
                      </div>
                      <div
                        style={{
                          opacity: 0.8,
                          color: "hsl(var(--foreground))",
                          fontSize: "16px",
                          fontWeight: 600,
                          fontFamily:
                            'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                        }}
                      >
                        {session.speakers ?? "Speaker"}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        onClick={e => {
                          e.stopPropagation(); // Prevent card selection when clicking toggle
                        }}
                      >
                        <ActiveToggleButton
                          table={contactLocationsSchema}
                          pk={session.id}
                          is_active={session.is_active}
                        />
                      </div>
                      <Button
                        type="text"
                        style={{
                          padding: "8px",
                          height: "40px",
                          width: "40px",
                          minWidth: "auto",
                          borderRadius: "50%",
                          border: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "background-color 0.2s ease",
                        }}
                        className={isSelected ? "bg-primary" : "bg-muted"}
                        onClick={e => {
                          e.stopPropagation();
                          handleEditWithSelection(session);
                        }}
                      >
                        <Edit2
                          size={16}
                          className={
                            isSelected
                              ? "text-primary-foreground"
                              : "text-primary"
                          }
                        />
                      </Button>
                    </div>
                  </div>

                  {/* Time Information */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div className={infoIconWrapperClassName}>
                      <img
                        src={clockBadgeIcon}
                        alt="Time"
                        style={infoIconImageStyle}
                      />
                    </div>
                    <span
                      style={{
                        opacity: 0.8,
                        color: "hsl(var(--foreground))",
                        fontSize: "16px",
                        fontWeight: 600,
                        fontFamily:
                          'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                        lineHeight: "24px",
                      }}
                    >
                      {session.starttime ?? "9:00 AM"} -{" "}
                      {session.endtime ?? "11:00 AM"}
                    </span>
                  </div>

                  {/* Date Information */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div className={infoIconWrapperClassName}>
                      <img
                        src={calendarBadgeIcon}
                        alt="Date"
                        style={infoIconImageStyle}
                      />
                    </div>
                    <span
                      style={{
                        opacity: 0.8,
                        color: "hsl(var(--foreground))",
                        fontSize: "16px",
                        fontWeight: 600,
                        fontFamily:
                          'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                        lineHeight: "24px",
                      }}
                    >
                      {session.date
                        ? (session.date as string)
                            .split("T")[0]
                            .split("-")
                            .reverse()
                            .join("-")
                        : "20-05-2025"}
                    </span>
                  </div>

                  {/* Venue Information */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {/* Map pin icon as placeholder - you can replace with actual venue icon */}
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: "hsl(var(--muted-foreground))",
                          borderRadius: "50%",
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            backgroundColor: "hsl(var(--background))",
                            borderRadius: "50%",
                            position: "absolute",
                            top: "4px",
                            left: "4px",
                          }}
                        />
                      </div>
                    </div>
                    <span
                      style={{
                        opacity: 0.8,
                        color: "hsl(var(--foreground))",
                        fontSize: "16px",
                        fontWeight: 600,
                        fontFamily:
                          'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                        lineHeight: "24px",
                      }}
                    >
                      {session.venue ?? "Main Hall"}
                    </span>
                  </div>

                  {/* Description Section */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        color: "hsl(var(--foreground))",
                        fontSize: "16px",
                        fontWeight: 500,
                        fontFamily:
                          'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                      }}
                    >
                      Description
                    </div>
                    <div
                      style={{
                        opacity: 0.8,
                        color: "hsl(var(--muted-foreground))",
                        fontSize: "14px",
                        fontWeight: 500,
                        fontFamily:
                          'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                        lineHeight: "1.4",
                      }}
                    >
                      {session.description ??
                        "Session details and description will appear here."}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const VolunteersCard: React.FC = () => {
    const { data: volunteersData, isLoading } = useQuery({
      queryKey: ["volunteers", historyMilestonesSchema, id, refreshKey],
      queryFn: () =>
        DynamicModuleService.getGridCoByConfig(
          historyMilestonesSchema || "",
          1,
          100, // Get more records for card view
          [
            "id",
            "activitytitle",
            "description",
            "fromtime",
            "totime",
            "fromdate",
            "todate",
            "is_active",
            "noofvolunteers",
            "applyvolunteers",
          ], // Include activity fields
          {
            jdid: id, // Use the chapter ID from URL params for filtering
          }
        ),
      enabled: !!historyMilestonesSchema && !!id,
      // Keep previous data to prevent blinking during updates
      placeholderData: previousData => previousData,
      // Use stale time to prevent unnecessary refetches
      staleTime: 1000,
    });

    // State to track selected card
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

    // No automatic refresh - updates are handled via optimistic state management
    // This prevents blinking and provides smooth updates

    // Handle edit volunteer with card selection
    const handleEditWithSelection = (volunteer: any) => {
      setSelectedCardId(volunteer.id);
      handleEdit(volunteer, historyMilestonesSchema);
    };

    if (isLoading) {
      return <div>Loading Activities...</div>;
    }

    // Extract volunteers array from volunteersData response
    // Handle different response structures: array, {results: []}, {data: []}
    const volunteers = (() => {
      if (!volunteersData) {
        return [];
      }
      // If it's already an array, return it
      if (Array.isArray(volunteersData)) {
        return volunteersData;
      }
      // If it has a results property (common pagination pattern)
      if (volunteersData.results && Array.isArray(volunteersData.results)) {
        return volunteersData.results;
      }
      // If it has a data property
      if (volunteersData.data && Array.isArray(volunteersData.data)) {
        return volunteersData.data;
      }
      // If it's an object but not an array, return empty array
      return [];
    })();

    return (
      <div className="mt-6">
        <div className="mb-4 text-xl font-semibold text-foreground">
          Activities
        </div>

        {volunteers.length === 0 ? (
          <div className="py-10 text-center text-base text-muted-foreground">
            No activities found. Add a new activity using the form above.
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "24px",
              padding: "0",
            }}
          >
            {volunteers.map((volunteer: any) => {
              // Check if this card is selected - also check if we're currently editing this volunteer
              const isSelected =
                selectedCardId === volunteer.id ||
                (isEditing &&
                  editingSchema === historyMilestonesSchema &&
                  editingRecordId === volunteer.id);

              return (
                <div
                  key={volunteer.id}
                  className={`flex min-h-[280px] w-full shrink-0 cursor-pointer flex-col gap-4 rounded-2xl p-5 shadow-md transition-all duration-300 md:w-[calc(50%-12px)] lg:w-[380px] ${
                    isSelected
                      ? "border-2 border-primary bg-card"
                      : "border border-primary/50 bg-card"
                  }`}
                  style={{ position: "relative" }}
                  onClick={() => {
                    // Toggle selection: if already selected, unselect; otherwise select
                    if (selectedCardId === volunteer.id) {
                      setSelectedCardId(null);
                    } else {
                      setSelectedCardId(volunteer.id);
                    }
                  }}
                >
                  {/* Header Section */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        opacity: 0.8,
                        color: "hsl(var(--foreground))",
                        fontSize: "20px",
                        fontWeight: 600,
                        fontFamily:
                          'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                        lineHeight: "32px",
                      }}
                    >
                      {volunteer.activitytitle ?? "Activity"}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        onClick={e => {
                          e.stopPropagation(); // Prevent card selection when clicking toggle
                        }}
                      >
                        <ActiveToggleButton
                          table={historyMilestonesSchema}
                          pk={volunteer.id}
                          is_active={volunteer.is_active}
                        />
                      </div>
                      {/* Volunteers count chip removed; see progress section below */}
                      <Button
                        type="text"
                        style={{
                          padding: "8px",
                          height: "40px",
                          width: "40px",
                          minWidth: "auto",
                          borderRadius: "50%",
                          border: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "background-color 0.2s ease",
                        }}
                        className={isSelected ? "bg-primary" : "bg-muted"}
                        onClick={e => {
                          e.stopPropagation(); // Prevent card selection when clicking edit button
                          handleEditWithSelection(volunteer);
                        }}
                        disabled={
                          isEditing && editingSchema !== historyMilestonesSchema
                        }
                      >
                        <Edit2
                          size={16}
                          className={
                            isSelected
                              ? "text-primary-foreground"
                              : "text-primary"
                          }
                        />
                      </Button>
                    </div>
                  </div>

                  {/* Progress Section */}
                  {(() => {
                    const parseNumber = (v: any): number => {
                      const n = Number(v);
                      return Number.isFinite(n) ? n : 0;
                    };
                    const parseAppliedFromAny = (v: any): number => {
                      if (Array.isArray(v) && v.length) {
                        return parseNumber(v[v.length - 1]);
                      }
                      if (typeof v === "string") {
                        const nums = v.match(/\d+/g)?.map(Number) ?? [];
                        return nums.length
                          ? Number.isFinite(nums[nums.length - 1])
                            ? nums[nums.length - 1]
                            : 0
                          : 0;
                      }
                      return parseNumber(v);
                    };

                    const totalCount = Math.max(
                      0,
                      parseNumber(volunteer.noofvolunteers)
                    );
                    let appliedCount = parseAppliedFromAny(
                      volunteer.applyvolunteers
                    );
                    if (
                      !Number.isFinite(appliedCount) ||
                      appliedCount < 0 ||
                      appliedCount > totalCount
                    ) {
                      const idStr = String(volunteer.id ?? "");
                      const seed = idStr
                        .split("")
                        .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
                      appliedCount = totalCount ? seed % (totalCount + 1) : 0;
                    }
                    const progressPercentage = totalCount
                      ? Math.round((appliedCount / totalCount) * 100)
                      : 0;

                    return (
                      <div className="flex w-full flex-col items-start justify-start gap-2 self-stretch sm:gap-3">
                        {/* Applied Volunteers Text */}
                        <div className="justify-start self-stretch break-words text-base leading-6 text-foreground sm:text-lg sm:leading-7">
                          Volunteers : {appliedCount}/{totalCount}
                        </div>
                        {/* Progress Bar with Gradient */}
                        <div className="relative h-2 w-full self-stretch overflow-hidden rounded-full bg-muted sm:h-2.5">
                          <div
                            className="absolute left-0 top-0 h-full rounded-full"
                            style={{
                              width: `${Math.min(progressPercentage, 100)}%`,
                              background:
                                "linear-gradient(to right, hsl(var(--accent)), hsl(var(--primary)))",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Time Information */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div className={infoIconWrapperClassName}>
                      <img
                        src={clockBadgeIcon}
                        alt="Time"
                        style={infoIconImageStyle}
                      />
                    </div>
                    <span
                      style={{
                        opacity: 0.8,
                        color: "hsl(var(--foreground))",
                        fontSize: "16px",
                        fontWeight: 600,
                        fontFamily:
                          'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                        lineHeight: "24px",
                      }}
                    >
                      {volunteer.fromtime ?? "9:00 AM"} -{" "}
                      {volunteer.totime ?? "11:00 AM"}
                    </span>
                  </div>

                  {/* Date Information */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div className={infoIconWrapperClassName}>
                      <img
                        src={calendarBadgeIcon}
                        alt="Date"
                        style={infoIconImageStyle}
                      />
                    </div>
                    <span
                      style={{
                        opacity: 0.8,
                        color: "hsl(var(--foreground))",
                        fontSize: "16px",
                        fontWeight: 600,
                        fontFamily:
                          'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                        lineHeight: "24px",
                      }}
                    >
                      {volunteer.fromdate
                        ? (volunteer.fromdate as string)
                            .split("T")[0]
                            .split("-")
                            .reverse()
                            .join("-")
                        : "20-05-2025"}{" "}
                      To{" "}
                      {volunteer.todate
                        ? (volunteer.todate as string)
                            .split("T")[0]
                            .split("-")
                            .reverse()
                            .join("-")
                        : "22-05-2025"}
                    </span>
                  </div>

                  {/* Description Section */}
                  <div
                    style={{
                      opacity: 0.8,
                      color: "hsl(var(--muted-foreground))",
                      fontSize: "16px",
                      fontWeight: 500,
                      fontFamily:
                        'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                      lineHeight: "24px",
                    }}
                  >
                    {volunteer.description ??
                      "Borem ipsum dolor sit amet, consectetur adipiscing elit."}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // const columnsmilestones: ProColumns<any>[] = [
  //   {
  //     title: "ID",
  //     dataIndex: "id",
  //   },

  //   {
  //     title: "Actions",
  //     width: 100,
  //     valueType: "option",
  //     render: (_, record) => [
  //       <CustomButton
  //         key="edit"
  //         icon={<Edit2 size={16} />}
  //         onClick={() => handleEdit(record, historyMilestonesSchema)}
  //         type="text"
  //         disabled={isEditing && editingSchema !== historyMilestonesSchema}
  //       />,
  //       <Popconfirm
  //         title="Are you sure you want to delete this milestone?"
  //         onConfirm={() => handleDelete(record.id, historyMilestonesSchema)}
  //         okText="Yes"
  //         key="delete"
  //         cancelText="No"
  //         disabled={isEditing && editingSchema !== historyMilestonesSchema}
  //       >
  //         <CustomButton
  //           danger
  //           key="delete"
  //           icon={<Trash size={16} />}
  //           type="text"
  //           disabled={isEditing && editingSchema !== historyMilestonesSchema}
  //         />
  //       </Popconfirm>,
  //     ],
  //   },
  // ];

  const columnsnoticeboard: ProColumns<any>[] = [
    {
      title: "Badge Template",
      dataIndex: "badgetemplate",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, noticeBoardSchema)}
          type="text"
        />,
        <Popconfirm
          title="Are you sure you want to delete this notice?"
          onConfirm={() => handleDelete(record.id, noticeBoardSchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
          />
        </Popconfirm>,
      ],
    },
  ];

  const columnCommittee: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Email",
      dataIndex: "inviteemailtemplate",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, committeeLeadershipSchema)}
          type="text"
        />,
        <Popconfirm
          title="Are you sure you want to delete this committee member?"
          onConfirm={() => handleDelete(record.id, committeeLeadershipSchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
          />
        </Popconfirm>,
      ],
    },
  ];

  // const columndocuments: ProColumns<any>[] = [
  //   {
  //     title: "ID",
  //     dataIndex: "id",
  //     hidden: true,
  //   },
  //   {
  //     title: "File Title",
  //     dataIndex: "filetitle",
  //   },
  //   {
  //     title: "Visibility",
  //     dataIndex: "visibility",
  //   },
  //   {
  //     title: "Actions",
  //     width: 100,
  //     valueType: "option",
  //     render: (_, record) => [
  //       <CustomButton
  //         key="edit"
  //         icon={<Edit2 size={16} />}
  //         onClick={() => handleEdit(record, documentsSchema)}
  //         type="text"
  //       />,
  //       <Popconfirm
  //         title="Are you sure you want to delete this document?"
  //         onConfirm={() => handleDelete(record.id, documentsSchema)}
  //         okText="Yes"
  //         key="delete"
  //         cancelText="No"
  //       >
  //         <CustomButton
  //           danger
  //           key="delete"
  //           icon={<Trash size={16} />}
  //           type="text"
  //         />
  //       </Popconfirm>,
  //     ],
  //   },
  // ];
  // const columnsbanking: ProColumns<any>[] = [
  //   {
  //     title: "ID",
  //     dataIndex: "id",
  //     hidden: true,
  //   },
  //   {
  //     title: "Payment Getaway Name",
  //     dataIndex: "payment",
  //   },
  //   {
  //     title: "Marchant Getaway Id",
  //     dataIndex: "marchantgetway",
  //   },
  //   {
  //     title: "Api Secret Key",
  //     dataIndex: "apisecretkey",
  //   },
  //   {
  //     title: "Webhook Url",
  //     dataIndex: "webhook",
  //   },
  //   {
  //     title: "Actions",
  //     width: 100,
  //     valueType: "option",
  //     render: (_, record) => [
  //       <CustomButton
  //         key="edit"
  //         icon={<Edit2 size={16} />}
  //         onClick={() => handleEdit(record, bankingSchema)}
  //         type="text"
  //       />,
  //       <Popconfirm
  //         title="Are you sure you want to delete this payment gateway?"
  //         onConfirm={() => handleDelete(record.id, bankingSchema)}
  //         okText="Yes"
  //         key="delete"
  //         cancelText="No"
  //       >
  //         <CustomButton
  //           danger
  //           key="delete"
  //           icon={<Trash size={16} />}
  //           type="text"
  //         />
  //       </Popconfirm>,
  //     ],
  //   },
  // ];

  // const columnsFAQ: ProColumns<any>[] = [
  //   {
  //     title: "ID",
  //     dataIndex: "id",
  //     hidden: true,
  //   },
  //   {
  //     title: "Questions",
  //     dataIndex: "questions",
  //   },
  //   {
  //     title: "Answers",
  //     dataIndex: "answer",
  //   },
  //   {
  //     title: "Actions",
  //     width: 100,
  //     valueType: "option",
  //     render: (_, record) => [
  //       <CustomButton
  //         key="edit"
  //         icon={<Edit2 size={16} />}
  //         onClick={() => handleEdit(record, faqSchema)}
  //         type="text"
  //       />,
  //       <Popconfirm
  //         title="Are you sure you want to delete this FAQ?"
  //         onConfirm={() => handleDelete(record.id, faqSchema)}
  //         okText="Yes"
  //         key="delete"
  //         cancelText="No"
  //       >
  //         <CustomButton
  //           danger
  //           key="delete"
  //           icon={<Trash size={16} />}
  //           type="text"
  //         />
  //       </Popconfirm>,
  //     ],
  //   },
  // ];

  // Custom tab styling

  // Generate title based on mode and fname from URL
  const getPageTitle = () => {
    const values: any = formOverview?.values ?? {};
    // Use eventData as fallback when form values aren't loaded yet
    // Merge eventData with form values, with form values taking priority
    const dataSource = { ...(eventData ?? {}), ...values };

    // Use event name from form values if available, otherwise use fnameFromUrl, otherwise default
    const eventName =
      dataSource.event_name ??
      dataSource.eventname ??
      dataSource.name ??
      fnameFromUrl;
    const titleText = eventName ?? (isEditMode ? "Edit Event" : "Add Event");
    const start =
      dataSource.eventstartdate ??
      dataSource.event_start_date ??
      dataSource.startdate ??
      dataSource.start_date ??
      dataSource.fromdate ??
      dataSource.from_date ??
      undefined;
    const end =
      dataSource.eventenddate ??
      dataSource.event_end_date ??
      dataSource.enddate ??
      dataSource.end_date ??
      dataSource.todate ??
      dataSource.to_date ??
      undefined;
    const formatDate = (d?: any) => {
      try {
        if (!d) {
          return undefined;
        }
        const date =
          typeof d === "string" || typeof d === "number" ? new Date(d) : d;
        if (Number.isNaN(date?.getTime?.())) {
          return undefined;
        }
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        });
      } catch {
        return undefined;
      }
    };
    const startText = formatDate(start) ?? "-";
    const endText = formatDate(end) ?? "-";
    const audienceCount =
      dataSource.overallcapacity ??
      dataSource.overall_capacity ??
      dataSource.audience ??
      dataSource.attendees ??
      dataSource.capacity ??
      600;
    // Prioritize eventtype if available, otherwise fall back to mode/eventmode
    const modeText =
      dataSource.eventtype ??
      dataSource.event_type ??
      dataSource.mode ??
      dataSource.eventmode ??
      "Hybrid";
    const venueText = dataSource.venue ?? dataSource.location ?? "";
    return (
      <div
        style={{
          borderRadius: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "100vw",
          boxSizing: "border-box",
        }}
      >
        {/* Left Section */}
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <img
            src={MMLogo}
            alt="Member Logo"
            className="h-24 w-24 rounded-full"
          />
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <h2
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  margin: 0,
                  color: "hsl(var(--foreground))",
                  fontFamily:
                    'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji","Segoe UI Symbol", "Noto Color Emoji"',
                }}
              >
                {titleText}
              </h2>
              <div className="inline-flex h-9 items-center justify-start gap-2.5 rounded-[50px] bg-primary px-4 py-[5px]">
                <div className="justify-start text-sm font-semibold leading-5 text-primary-foreground">
                  {audienceCount} people
                </div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <div className="justify-start text-sm font-semibold text-foreground">
                {startText}- {endText}
              </div>
              <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              <div className="justify-start text-sm font-semibold text-foreground">
                {modeText}{" "}
                <span className="font-normal text-foreground">{venueText}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section: Buttons */}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: "10px",
            flexShrink: 0,
            paddingLeft: "380px",
          }}
        >
          {/* Mail Button */}
          <div
            style={{
              width: 40,
              height: 40,
              backgroundColor: "hsl(var(--primary))", // violet-100
              borderRadius: "62.50px",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = "hsl(var(--primary))";
              e.currentTarget.style.filter = "brightness(1.1)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = "hsl(var(--primary))";
              e.currentTarget.style.filter = "none";
            }}
          >
            <Mail
              size={20}
              style={{
                color: "hsl(var(--primary-foreground))",
              }}
            />
          </div>
        </div>
      </div>
    );
  };
  return (
    <div>
      {isEditMode ? (
        <PageHeader
          key={headerUpdateKey}
          className="custom-page-header"
          title={getPageTitle()}
          onBack={() => navigate("/mm" + MMNavigationRoutes.EVENTS_LIST)}
          extra={
            <WorkflowStatus
              schemaName={basicInfoSchema ?? ""}
              recordId={id ?? ""}
            />
          }
        />
      ) : (
        <PageHeader
          title="Add Event"
          onBack={() => navigate("/mm" + MMNavigationRoutes.EVENTS_LIST)}
        />
      )}

      <Tabs
        items={
          isEditMode
            ? [
                {
                  key: "1",
                  label: (
                    <span className={tabLabelClassName}>Event Information</span>
                  ),
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formOverview}
                        schemaName={basicInfoSchema ?? ""}
                        recordId={id}
                        ref={formOverviewRef}
                      />
                      <div className="flex justify-end gap-4">
                        <Button
                          type="primary"
                          onClick={async () => {
                            try {
                              await formOverviewRef.current?.handleSave();

                              // Get the updated form values
                              const updatedValues = formOverview.values;

                              // Immediately update form initial values and current values
                              // This ensures the header updates right away
                              formOverview.setInitialValues(updatedValues);
                              formOverview.setValues(updatedValues);

                              // Force header re-render by updating state
                              setHeaderUpdateKey(prev => prev + 1);

                              // Invalidate the query to refetch the updated event data from server
                              // This ensures data consistency
                              queryClient.invalidateQueries({
                                queryKey: [
                                  "dynamic-model",
                                  basicInfoSchema,
                                  id,
                                ],
                              });

                              refreshTableData();
                              // notify.success(
                              //   "Organization updated successfully"
                              // );
                            } catch (error) {
                              console.error("Update error:", error);
                              // Validation error message is already shown by SchemaFormAutoSubmit
                            }
                          }}
                        >
                          Update
                        </Button>
                      </div>
                    </>
                  ),
                },

                {
                  key: "3",
                  label: <span className={tabLabelClassName}>Tickets</span>,
                  children: (
                    <>
                      {/* Add Ticket Form */}
                      <SchemaFormAutoSubmit
                        form={formMembershipTypes}
                        schemaName={membershipTypesSchema || ""}
                        recordId={
                          isEditing && editingSchema === membershipTypesSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formMembershipTypesRef}
                      />

                      <div className="mb-4 flex justify-end gap-4">
                        {isEditing &&
                        editingSchema === membershipTypesSchema ? (
                          <>
                            <Button
                              type="primary"
                              loading={savingTicket}
                              disabled={savingTicket}
                              onClick={async () => {
                                if (savingTicket) {
                                  return;
                                } // Prevent double-click
                                try {
                                  setSavingTicket(true);
                                  if (id) {
                                    formMembershipTypes.setValues({ jdid: id });
                                  }
                                  const formValues = formMembershipTypes.values;
                                  const jdid = formValues.jdid;

                                  if (id && String(id) === String(jdid ?? "")) {
                                    // Get the saved values for optimistic update
                                    const savedValues =
                                      formMembershipTypes.values;

                                    // Save to API first
                                    await formMembershipTypesRef.current?.handleSave();

                                    // Update the query cache immediately after successful save
                                    queryClient.setQueryData(
                                      [
                                        "membershipTypes",
                                        membershipTypesSchema,
                                        id,
                                        refreshKey,
                                      ],
                                      (oldData: any) => {
                                        if (!oldData) {
                                          return oldData;
                                        }

                                        const dataArray = Array.isArray(oldData)
                                          ? oldData
                                          : (oldData?.results ?? []);

                                        return dataArray.map((item: any) => {
                                          if (item.id === editingRecordId) {
                                            return {
                                              ...item,
                                              category:
                                                savedValues.category ??
                                                item.category,
                                              benefits:
                                                savedValues.benefits ??
                                                item.benefits,
                                              price:
                                                savedValues.price ?? item.price,
                                              ticket:
                                                savedValues.ticket ??
                                                item.ticket,
                                              is_active:
                                                savedValues.is_active ??
                                                item.is_active,
                                            };
                                          }
                                          return item;
                                        });
                                      }
                                    );

                                    handleCancelEdit();
                                    resetFormMembershipTypes();
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                } finally {
                                  setSavingTicket(false);
                                }
                              }}
                            >
                              Update Ticket
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            loading={savingTicket}
                            disabled={savingTicket}
                            onClick={async () => {
                              if (savingTicket) {
                                return;
                              } // Prevent double-click
                              try {
                                setSavingTicket(true);
                                if (id) {
                                  formMembershipTypes.setValues({ jdid: id });
                                }
                                const formValues = formMembershipTypes.values;
                                const jdid = formValues.jdid;

                                if (id && String(id) === String(jdid ?? "")) {
                                  // Get the saved values for optimistic update
                                  const savedValues =
                                    formMembershipTypes.values;

                                  // Save to API first and get the response with real ID
                                  const response =
                                    (await formMembershipTypesRef.current?.handleSave()) as any;

                                  // Update the query cache immediately after successful save
                                  queryClient.setQueryData(
                                    [
                                      "membershipTypes",
                                      membershipTypesSchema,
                                      id,
                                      refreshKey,
                                    ],
                                    (oldData: any) => {
                                      if (!oldData) {
                                        return oldData;
                                      }

                                      const dataArray = Array.isArray(oldData)
                                        ? oldData
                                        : (oldData?.results ?? []);

                                      // For new tickets, add to the array with real ID from API response
                                      const newTicket = {
                                        id:
                                          response?.id ??
                                          savedValues.id ??
                                          Date.now().toString(),
                                        category: savedValues.category,
                                        benefits: savedValues.benefits,
                                        price: savedValues.price,
                                        ticket: savedValues.ticket,
                                        soldticket: savedValues.soldticket ?? 0,
                                        is_active:
                                          savedValues.is_active ?? true,
                                      };

                                      return [...dataArray, newTicket];
                                    }
                                  );

                                  resetFormMembershipTypes();
                                  // Refresh the data to ensure we have the latest from server
                                  queryClient.invalidateQueries({
                                    queryKey: [
                                      "membershipTypes",
                                      membershipTypesSchema,
                                      id,
                                    ],
                                  });
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              } finally {
                                setSavingTicket(false);
                              }
                            }}
                          >
                            Save Ticket
                          </Button>
                        )}
                      </div>

                      <MembershipTypesCard
                        selectedCategory={selectedTicketCategoryFilter}
                        onCategorySelect={setSelectedTicketCategoryFilter}
                      />

                      {/* Tickets Table */}
                      <div style={{ marginTop: 50, width: "100%" }}>
                        <div className="flex w-full flex-col items-start justify-start gap-3 overflow-x-auto rounded-2xl bg-card p-5">
                          {ticketsLoading ? (
                            <div className="flex w-full items-center justify-center py-8">
                              <div className="text-base font-medium text-gray-500">
                                Loading tickets...
                              </div>
                            </div>
                          ) : (
                            <div className="flex w-full min-w-[1200px] flex-col items-start justify-start gap-px">
                              {/* Header Row */}
                              <div className="flex w-full items-center justify-start">
                                <div className="flex h-11 min-w-[80px] flex-[0.6] items-center justify-center gap-2.5 rounded-bl-md rounded-tl-md bg-muted px-3.5 py-2">
                                  <div
                                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                                    style={{
                                      textAlign: "center",
                                    }}
                                  >
                                    SR NO
                                  </div>
                                </div>
                                <div className="flex h-11 min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                                  <div
                                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                                    style={{
                                      textAlign: "center",
                                    }}
                                  >
                                    MEMBER NAME
                                  </div>
                                </div>
                                <div className="flex h-11 min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                                  <div
                                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                                    style={{
                                      textAlign: "center",
                                    }}
                                  >
                                    MEMBER PHONE
                                  </div>
                                </div>
                                {!selectedTicketCategoryFilter && (
                                  <div className="flex h-11 min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                                    <div
                                      className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                                      style={{
                                        textAlign: "center",
                                      }}
                                    >
                                      TICKET CATEGORY
                                    </div>
                                  </div>
                                )}
                                <div className="flex h-11 min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                                  <div
                                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                                    style={{
                                      textAlign: "center",
                                    }}
                                  >
                                    TICKETS BOUGHT
                                  </div>
                                </div>
                                <div className="flex h-11 min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                                  <div
                                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                                    style={{
                                      textAlign: "center",
                                    }}
                                  >
                                    AMOUNT
                                  </div>
                                </div>
                                <div className="flex h-11 min-w-[120px] flex-[1] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                                  <div
                                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                                    style={{
                                      textAlign: "center",
                                    }}
                                  >
                                    PAYMENT STATUS
                                  </div>
                                </div>
                                <div className="flex h-11 min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                                  <div
                                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                                    style={{
                                      textAlign: "center",
                                    }}
                                  >
                                    PURCHASE DATE
                                  </div>
                                </div>
                                <div className="flex h-11 min-w-[100px] flex-[0.8] items-center justify-center gap-2.5 rounded-br-md rounded-tr-md bg-muted px-3.5 py-2">
                                  <div
                                    className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground"
                                    style={{
                                      textAlign: "center",
                                    }}
                                  >
                                    ACTION
                                  </div>
                                </div>
                              </div>

                              {/* Data Rows */}
                              {filteredTickets.length > 0 ? (
                                filteredTickets.map((record, index) => {
                                  const getStatusBadge = (status: string) => {
                                    if (
                                      status === "Paid" ||
                                      status === "paid"
                                    ) {
                                      return (
                                        <div className="flex items-center justify-center gap-2.5 rounded-[500px] bg-green-100 px-3 py-1.5">
                                          <div className="text-xs font-semibold leading-6 text-lime-800 opacity-80">
                                            Paid
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div className="flex items-center justify-center gap-2.5 rounded-[500px] bg-yellow-50 px-3 py-1.5">
                                          <div className="text-xs font-semibold leading-6 text-yellow-600 opacity-80">
                                            {status || "Pending"}
                                          </div>
                                        </div>
                                      );
                                    }
                                  };

                                  const ticketCategory =
                                    getTicketCategoryLabel(record);

                                  return (
                                    <div
                                      key={record.buy_ticket_id ?? index}
                                      className="flex w-full items-start justify-start border-b border-gray-100 last:border-b-0"
                                    >
                                      <div className="flex min-h-[60px] min-w-[80px] flex-[0.6] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                        <div
                                          className="text-sm font-medium leading-6 text-gray-500"
                                          style={{
                                            wordBreak: "break-word",
                                            overflowWrap: "break-word",
                                            textAlign: "center",
                                            width: "100%",
                                          }}
                                        >
                                          {index + 1}
                                        </div>
                                      </div>
                                      <div className="flex min-h-[60px] min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                        <div
                                          className="text-sm font-medium leading-6 text-gray-500"
                                          style={{
                                            wordBreak: "break-word",
                                            overflowWrap: "break-word",
                                            textAlign: "center",
                                            width: "100%",
                                          }}
                                        >
                                          {record.member_name ?? "N/A"}
                                        </div>
                                      </div>
                                      <div className="flex min-h-[60px] min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                        <div
                                          className="text-sm font-medium leading-6 text-gray-500"
                                          style={{
                                            wordBreak: "break-word",
                                            overflowWrap: "break-word",
                                            textAlign: "center",
                                            width: "100%",
                                          }}
                                        >
                                          {record.member_phonenumber ?? "N/A"}
                                        </div>
                                      </div>
                                      {!selectedTicketCategoryFilter && (
                                        <div className="flex min-h-[60px] min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                          <div
                                            className="text-sm font-medium leading-6 text-gray-500"
                                            style={{
                                              wordBreak: "break-word",
                                              overflowWrap: "break-word",
                                              textAlign: "center",
                                              width: "100%",
                                            }}
                                          >
                                            {ticketCategory ?? "N/A"}
                                          </div>
                                        </div>
                                      )}
                                      <div className="flex min-h-[60px] min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                        <div
                                          className="text-sm font-medium leading-6 text-gray-500"
                                          style={{
                                            wordBreak: "break-word",
                                            overflowWrap: "break-word",
                                            textAlign: "center",
                                            width: "100%",
                                          }}
                                        >
                                          {record.tickets_bought ?? 0}
                                        </div>
                                      </div>
                                      <div className="flex min-h-[60px] min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                        <div
                                          className="text-sm font-medium leading-6 text-gray-500"
                                          style={{
                                            wordBreak: "break-word",
                                            overflowWrap: "break-word",
                                            textAlign: "center",
                                            width: "100%",
                                          }}
                                        >
                                          {record.amount_usd ?? "$0.00"}
                                        </div>
                                      </div>
                                      <div className="flex min-h-[60px] min-w-[120px] flex-[1] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                        {getStatusBadge(record.payment_status)}
                                      </div>
                                      <div className="flex min-h-[60px] min-w-[140px] flex-[1.2] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                        <div
                                          className="text-sm font-medium leading-6 text-gray-500"
                                          style={{
                                            wordBreak: "break-word",
                                            overflowWrap: "break-word",
                                            textAlign: "center",
                                            width: "100%",
                                          }}
                                        >
                                          {record.purchase_date ?? "N/A"}
                                        </div>
                                      </div>
                                      <div className="flex min-h-[60px] min-w-[100px] flex-[0.8] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                        <button
                                          className="inline-flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-[52.50px] bg-violet-100 transition-colors hover:bg-violet-200"
                                          onClick={() => {
                                            // Handle view action - you can add navigation or modal here
                                            console.log(
                                              "View ticket:",
                                              record.buy_ticket_id
                                            );
                                          }}
                                        >
                                          <img
                                            src={eyeIcon}
                                            alt="View"
                                            className="h-7 w-7 object-contain"
                                          />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="flex w-full items-center justify-center py-8">
                                  <div className="text-base font-medium text-gray-500">
                                    {selectedTicketCategoryFilter
                                      ? "No tickets found for the selected category."
                                      : "No tickets purchased yet"}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ),
                },
                {
                  key: "2",
                  label: <span className={tabLabelClassName}>Volunteers</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formHistoryMilestones}
                        schemaName={historyMilestonesSchema || ""}
                        recordId={
                          isEditing && editingSchema === historyMilestonesSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formHistoryMilestonesRef}
                      />
                      <div className="mb-4 flex justify-end gap-4">
                        {isEditing &&
                        editingSchema === historyMilestonesSchema ? (
                          <>
                            <Button
                              type="primary"
                              loading={savingVolunteer}
                              disabled={savingVolunteer}
                              onClick={async () => {
                                if (savingVolunteer) {
                                  return;
                                } // Prevent double-click
                                try {
                                  setSavingVolunteer(true);
                                  if (id) {
                                    formHistoryMilestones.setValues({
                                      jdid: id,
                                    });
                                  }
                                  const formValues =
                                    formHistoryMilestones.values;
                                  const jdid = formValues.jdid;

                                  if (id && String(id) === String(jdid ?? "")) {
                                    // Get the saved values for optimistic update
                                    const savedValues =
                                      formHistoryMilestones.values;

                                    // Save to API first
                                    await formHistoryMilestonesRef.current?.handleSave();

                                    // Update the query cache immediately after successful save
                                    queryClient.setQueryData(
                                      [
                                        "volunteers",
                                        historyMilestonesSchema,
                                        id,
                                        refreshKey,
                                      ],
                                      (oldData: any) => {
                                        if (!oldData) {
                                          return oldData;
                                        }

                                        const dataArray = Array.isArray(oldData)
                                          ? oldData
                                          : (oldData?.results ?? []);

                                        return dataArray.map((item: any) => {
                                          if (item.id === editingRecordId) {
                                            return {
                                              ...item,
                                              activitytitle:
                                                savedValues.activitytitle ??
                                                item.activitytitle,
                                              description:
                                                savedValues.description ??
                                                item.description,
                                              fromtime:
                                                savedValues.fromtime ??
                                                item.fromtime,
                                              totime:
                                                savedValues.totime ??
                                                item.totime,
                                              fromdate:
                                                savedValues.fromdate ??
                                                item.fromdate,
                                              todate:
                                                savedValues.todate ??
                                                item.todate,
                                              noofvolunteers:
                                                savedValues.noofvolunteers ??
                                                item.noofvolunteers,
                                              applyvolunteers:
                                                savedValues.applyvolunteers ??
                                                item.applyvolunteers,
                                              is_active:
                                                savedValues.is_active ??
                                                item.is_active,
                                            };
                                          }
                                          return item;
                                        });
                                      }
                                    );

                                    handleCancelEdit();
                                    resetFormHistoryMilestones();
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                } finally {
                                  setSavingVolunteer(false);
                                }
                              }}
                            >
                              Update Activity
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            icon={<Plus size={16} />}
                            loading={savingVolunteer}
                            disabled={savingVolunteer}
                            onClick={async () => {
                              if (savingVolunteer) {
                                return;
                              } // Prevent double-click
                              try {
                                setSavingVolunteer(true);
                                if (id) {
                                  formHistoryMilestones.setValues({
                                    jdid: id,
                                  });
                                }
                                const formValues = formHistoryMilestones.values;
                                const jdid = formValues.jdid;

                                if (id && String(id) === String(jdid ?? "")) {
                                  // Get the saved values for optimistic update
                                  const savedValues =
                                    formHistoryMilestones.values;

                                  // Save to API first and get the response with real ID
                                  const response =
                                    (await formHistoryMilestonesRef.current?.handleSave()) as any;

                                  // Update the query cache immediately after successful save
                                  queryClient.setQueryData(
                                    [
                                      "volunteers",
                                      historyMilestonesSchema,
                                      id,
                                      refreshKey,
                                    ],
                                    (oldData: any) => {
                                      if (!oldData) {
                                        return oldData;
                                      }

                                      const dataArray = Array.isArray(oldData)
                                        ? oldData
                                        : (oldData?.results ?? []);

                                      // For new volunteers, add to the array with real ID from API response
                                      const newVolunteer = {
                                        id:
                                          response?.id ??
                                          savedValues.id ??
                                          Date.now().toString(),
                                        activitytitle:
                                          savedValues.activitytitle,
                                        description: savedValues.description,
                                        fromtime: savedValues.fromtime,
                                        totime: savedValues.totime,
                                        fromdate: savedValues.fromdate,
                                        todate: savedValues.todate,
                                        noofvolunteers:
                                          savedValues.noofvolunteers ?? 0,
                                        applyvolunteers:
                                          savedValues.applyvolunteers ?? 0,
                                        is_active:
                                          savedValues.is_active ?? true,
                                      };

                                      return [...dataArray, newVolunteer];
                                    }
                                  );

                                  resetFormHistoryMilestones();
                                  // Refresh the data to ensure we have the latest from server
                                  queryClient.invalidateQueries({
                                    queryKey: [
                                      "volunteers",
                                      historyMilestonesSchema,
                                      id,
                                    ],
                                  });
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              } finally {
                                setSavingVolunteer(false);
                              }
                            }}
                          >
                            Add Activity
                          </Button>
                        )}
                      </div>

                      <VolunteersCard />

                      {/* Volunteer Signups Table */}
                      <div style={{ marginTop: 50, width: "100%" }}>
                        <div className="flex w-full flex-col items-start justify-start gap-3 overflow-x-auto rounded-2xl bg-card p-5">
                          <div className="mb-2 text-xl font-semibold text-foreground">
                            Volunteer Signups
                          </div>
                          {signupsLoading ? (
                            <div className="flex w-full items-center justify-center py-8">
                              <div className="text-base font-medium text-gray-500">
                                Loading signups...
                              </div>
                            </div>
                          ) : (
                            <div className="flex w-full min-w-[1000px] flex-col items-start justify-start gap-px">
                              {/* Header Row */}
                              <div className="flex w-full items-center justify-start">
                                <div className="flex h-11 min-w-[80px] flex-[0.6] items-center justify-center gap-2.5 rounded-bl-md rounded-tl-md bg-muted px-3.5 py-2">
                                  <div className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground">
                                    SR NO
                                  </div>
                                </div>
                                <div className="flex h-11 min-w-[220px] flex-[1.8] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                                  <div className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground">
                                    ACTIVITY TITLE
                                  </div>
                                </div>
                                <div className="flex h-11 min-w-[180px] flex-[1.4] items-center justify-center gap-2.5 bg-muted px-3.5 py-2">
                                  <div className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground">
                                    NO OF VOLUNTEERS
                                  </div>
                                </div>
                                <div className="flex h-11 min-w-[180px] flex-[1.4] items-center justify-center gap-2.5 rounded-br-md rounded-tr-md bg-muted px-3.5 py-2">
                                  <div className="whitespace-nowrap text-sm font-medium uppercase text-muted-foreground">
                                    APPLY VOLUNTEERS
                                  </div>
                                </div>
                              </div>

                              {/* Data Rows */}
                              {volunteerSignups.length > 0 ? (
                                volunteerSignups.map((record, index) => (
                                  <div
                                    key={record.id ?? index}
                                    className="flex w-full items-start justify-start border-b border-gray-100 last:border-b-0"
                                  >
                                    <div className="flex min-h-[60px] min-w-[80px] flex-[0.6] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                      <div className="text-sm font-medium leading-6 text-gray-500">
                                        {index + 1}
                                      </div>
                                    </div>
                                    <div className="flex min-h-[60px] min-w-[220px] flex-[1.8] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                      <div className="text-sm font-medium leading-6 text-gray-500">
                                        {record.activity_title ?? "N/A"}
                                      </div>
                                    </div>
                                    <div className="flex min-h-[60px] min-w-[180px] flex-[1.4] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                      <div className="text-sm font-medium leading-6 text-gray-500">
                                        {record.no_of_volunteers ?? "0"}
                                      </div>
                                    </div>
                                    <div className="flex min-h-[60px] min-w-[180px] flex-[1.4] items-center justify-center gap-2.5 self-stretch bg-white px-3.5 py-3">
                                      <div className="text-sm font-medium leading-6 text-gray-500">
                                        {record.apply_volunteers ?? "0"}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="flex w-full items-center justify-center py-8">
                                  <div className="text-base font-medium text-gray-500">
                                    No volunteer signups found.
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ),
                },
                {
                  key: "5",
                  label: (
                    <span className={tabLabelClassName}>Agenda & Sessions</span>
                  ),
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formContactLocations}
                        schemaName={contactLocationsSchema || ""}
                        recordId={
                          isEditing && editingSchema === contactLocationsSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formContactLocationsRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing &&
                        editingSchema === contactLocationsSchema ? (
                          <>
                            <Button
                              type="primary"
                              loading={savingSession}
                              disabled={savingSession}
                              onClick={async () => {
                                if (savingSession) {
                                  return;
                                } // Prevent double-click
                                try {
                                  setSavingSession(true);
                                  if (id) {
                                    formContactLocations.setValues({
                                      jdid: id,
                                    });
                                  }
                                  const formValues =
                                    formContactLocations.values;
                                  const jdid = formValues.jdid;

                                  if (id && String(id) === String(jdid ?? "")) {
                                    // Get the saved values for optimistic update
                                    const savedValues =
                                      formContactLocations.values;

                                    // Save to API first
                                    await formContactLocationsRef.current?.handleSave();

                                    // Update the query cache immediately after successful save
                                    queryClient.setQueryData(
                                      [
                                        "sessions",
                                        contactLocationsSchema,
                                        id,
                                        refreshKey,
                                      ],
                                      (oldData: any) => {
                                        if (!oldData) {
                                          return oldData;
                                        }

                                        const dataArray = Array.isArray(oldData)
                                          ? oldData
                                          : (oldData?.results ?? []);

                                        return dataArray.map((item: any) => {
                                          if (item.id === editingRecordId) {
                                            return {
                                              ...item,
                                              activityname:
                                                savedValues.activityname ??
                                                item.activityname,
                                              starttime:
                                                savedValues.starttime ??
                                                item.starttime,
                                              endtime:
                                                savedValues.endtime ??
                                                item.endtime,
                                              date:
                                                savedValues.date ?? item.date,
                                              speakers:
                                                savedValues.speakers ??
                                                item.speakers,
                                              venue:
                                                savedValues.venue ?? item.venue,
                                              description:
                                                savedValues.description ??
                                                item.description,
                                              is_active:
                                                savedValues.is_active ??
                                                item.is_active,
                                            };
                                          }
                                          return item;
                                        });
                                      }
                                    );

                                    handleCancelEdit();
                                    resetFormContactLocations();
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                } finally {
                                  setSavingSession(false);
                                }
                              }}
                            >
                              Update Session
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            loading={savingSession}
                            disabled={savingSession}
                            onClick={async () => {
                              if (savingSession) {
                                return;
                              } // Prevent double-click
                              try {
                                setSavingSession(true);
                                if (id) {
                                  formContactLocations.setValues({
                                    jdid: id,
                                  });
                                }
                                const formValues = formContactLocations.values;
                                const jdid = formValues.jdid;

                                if (id && String(id) === String(jdid ?? "")) {
                                  // Get the saved values for optimistic update
                                  const savedValues =
                                    formContactLocations.values;

                                  // Save to API first and get the response with real ID
                                  const response =
                                    (await formContactLocationsRef.current?.handleSave()) as any;

                                  // Update the query cache immediately after successful save
                                  queryClient.setQueryData(
                                    [
                                      "sessions",
                                      contactLocationsSchema,
                                      id,
                                      refreshKey,
                                    ],
                                    (oldData: any) => {
                                      if (!oldData) {
                                        return oldData;
                                      }

                                      const dataArray = Array.isArray(oldData)
                                        ? oldData
                                        : (oldData?.results ?? []);

                                      // For new sessions, add to the array with real ID from API response
                                      const newSession = {
                                        id:
                                          response?.id ??
                                          savedValues.id ??
                                          Date.now().toString(),
                                        activityname: savedValues.activityname,
                                        starttime: savedValues.starttime,
                                        endtime: savedValues.endtime,
                                        date: savedValues.date,
                                        speakers: savedValues.speakers,
                                        venue: savedValues.venue,
                                        description: savedValues.description,
                                        is_active:
                                          savedValues.is_active ?? true,
                                      };

                                      return [...dataArray, newSession];
                                    }
                                  );

                                  resetFormContactLocations();
                                  // Refresh the data to ensure we have the latest from server
                                  queryClient.invalidateQueries({
                                    queryKey: [
                                      "sessions",
                                      contactLocationsSchema,
                                      id,
                                    ],
                                  });
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                              } finally {
                                setSavingSession(false);
                              }
                            }}
                          >
                            Save Session
                          </Button>
                        )}
                      </div>

                      {/* Agenda & Sessions Cards */}
                      <AgendaSessionsCard />
                    </>
                  ),
                },

                {
                  key: "8",
                  label: <span className={tabLabelClassName}>Donation</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formHoursStatus}
                        schemaName={hoursstatus || ""}
                        recordId={
                          isEditing && editingSchema === hoursstatus
                            ? editingRecordId
                            : ""
                        }
                        ref={formHoursStatusRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing && editingSchema === hoursstatus ? (
                          <>
                            <Button
                              type="primary"
                              loading={savingDonation}
                              disabled={savingDonation}
                              onClick={async () => {
                                if (savingDonation) {
                                  return;
                                } // Prevent double-click
                                try {
                                  setSavingDonation(true);
                                  if (id) {
                                    formHoursStatus.setValues({ jdid: id });
                                  }
                                  const formValues = formHoursStatus.values;
                                  const jdid = formValues.jdid;

                                  if (id && String(id) === String(jdid ?? "")) {
                                    // Get the saved values for optimistic update
                                    const savedValues = formHoursStatus.values;

                                    // Save to API first
                                    await formHoursStatusRef.current?.handleSave();

                                    // Update the query cache immediately after successful save
                                    queryClient.setQueryData(
                                      [
                                        "donations",
                                        hoursstatus,
                                        id,
                                        refreshKey,
                                      ],
                                      (oldData: any) => {
                                        if (!oldData) {
                                          return oldData;
                                        }

                                        const dataArray = Array.isArray(oldData)
                                          ? oldData
                                          : (oldData?.results ?? []);

                                        return dataArray.map((item: any) => {
                                          if (item.id === editingRecordId) {
                                            return {
                                              ...item,
                                              donationamountneeded:
                                                savedValues.donationamountneeded ??
                                                item.donationamountneeded,
                                              donationamountraised:
                                                savedValues.donationamountraised ??
                                                item.donationamountraised,
                                              donationtitle:
                                                savedValues.donationtitle ??
                                                item.donationtitle,
                                              category:
                                                savedValues.category ??
                                                item.category,
                                              descripction:
                                                savedValues.descripction ??
                                                item.descripction,
                                              fromdate:
                                                savedValues.fromdate ??
                                                item.fromdate,
                                              todate:
                                                savedValues.todate ??
                                                item.todate,
                                            };
                                          }
                                          return item;
                                        });
                                      }
                                    );

                                    handleCancelEdit();
                                    resetFormHoursStatus();
                                    // notify.success(
                                    //   "Hours & status updated successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                } finally {
                                  setSavingDonation(false);
                                }
                              }}
                            >
                              Update Donation
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            loading={savingDonation}
                            disabled={savingDonation}
                            onClick={async () => {
                              if (savingDonation) {
                                return;
                              } // Prevent double-click
                              try {
                                setSavingDonation(true);
                                if (id) {
                                  formHoursStatus.setValues({ jdid: id });
                                }
                                const formValues = formHoursStatus.values;
                                const jdid = formValues.jdid;

                                if (id && String(id) === String(jdid ?? "")) {
                                  // Get the saved values for optimistic update
                                  const savedValues = formHoursStatus.values;

                                  // Save to API first and get the response with real ID
                                  const response =
                                    (await formHoursStatusRef.current?.handleSave()) as any;

                                  // Update the query cache immediately after successful save
                                  queryClient.setQueryData(
                                    ["donations", hoursstatus, id, refreshKey],
                                    (oldData: any) => {
                                      if (!oldData) {
                                        return oldData;
                                      }

                                      const dataArray = Array.isArray(oldData)
                                        ? oldData
                                        : (oldData?.results ?? []);

                                      // For new donations, add to the array with real ID from API response
                                      const newDonation = {
                                        id:
                                          response?.id ??
                                          savedValues.id ??
                                          Date.now().toString(),
                                        donationtitle:
                                          savedValues.donationtitle,
                                        category: savedValues.category,
                                        donationamountneeded:
                                          savedValues.donationamountneeded,
                                        donationamountraised:
                                          savedValues.donationamountraised,
                                        descripction: savedValues.descripction,
                                        fromdate: savedValues.fromdate,
                                        todate: savedValues.todate,
                                        is_active:
                                          savedValues.is_active ?? true,
                                      };

                                      return [...dataArray, newDonation];
                                    }
                                  );

                                  resetFormHoursStatus();
                                  // Refresh the data to ensure we have the latest from server
                                  queryClient.invalidateQueries({
                                    queryKey: ["donations", hoursstatus, id],
                                  });
                                  // notify.success(
                                  //   "Hours & status saved successfully"
                                  // );
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              } finally {
                                setSavingDonation(false);
                              }
                            }}
                          >
                            Save Donation
                          </Button>
                        )}
                      </div>
                      {/* Donation Table */}
                      <DonationCard
                        selectedCategory={selectedDonationCategoryFilter}
                        onCategorySelect={setSelectedDonationCategoryFilter}
                      />
                    </>
                  ),
                },

                {
                  key: "9",
                  label: <span className={tabLabelClassName}>Sponsorship</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formPhotoGallery}
                        schemaName={photoGallerySchema || ""}
                        recordId={
                          isEditing && editingSchema === photoGallerySchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formPhotoGalleryRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing && editingSchema === photoGallerySchema ? (
                          <>
                            <Button
                              type="primary"
                              loading={savingSponsorship}
                              disabled={savingSponsorship}
                              onClick={async () => {
                                if (savingSponsorship) {
                                  return;
                                } // Prevent double-click
                                try {
                                  setSavingSponsorship(true);
                                  if (id) {
                                    formPhotoGallery.setValues({ jdid: id });
                                  }
                                  const formValues = formPhotoGallery.values;
                                  const jdid = formValues.jdid;

                                  if (id && String(id) === String(jdid ?? "")) {
                                    await formPhotoGalleryRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormPhotoGallery();
                                    // notify.success(
                                    //   "Gallery image updated successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                } finally {
                                  setSavingSponsorship(false);
                                }
                              }}
                            >
                              Update Sponsorship
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            loading={savingSponsorship}
                            disabled={savingSponsorship}
                            onClick={async () => {
                              if (savingSponsorship) {
                                return;
                              } // Prevent double-click
                              try {
                                setSavingSponsorship(true);
                                if (id) {
                                  formPhotoGallery.setValues({ jdid: id });
                                }
                                const formValues = formPhotoGallery.values;
                                const jdid = formValues.jdid;

                                if (id && String(id) === String(jdid ?? "")) {
                                  await formPhotoGalleryRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormPhotoGallery();
                                  // notify.success(
                                  //   "Gallery image saved successfully"
                                  // );
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              } finally {
                                setSavingSponsorship(false);
                              }
                            }}
                          >
                            Save Sponsorship
                          </Button>
                        )}
                      </div>

                      {showAddPhotoGallery && (
                        <>
                          <SchemaFormAutoSubmit
                            form={formAddPhotoGallery}
                            schemaName={addphotogallery || ""}
                            recordId={
                              isEditing && editingSchema === addphotogallery
                                ? editingRecordId
                                : ""
                            }
                            ref={formAddPhotoGalleryRef}
                          />
                          <div className="flex justify-end gap-4">
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  if (id) {
                                    formAddPhotoGallery.setValues({
                                      jdid: id,
                                    });
                                  }
                                  const formValues = formAddPhotoGallery.values;
                                  const jdid = formValues.jdid;

                                  if (id && String(id) === String(jdid ?? "")) {
                                    await formAddPhotoGalleryRef.current?.handleSave();
                                    refreshTableData();
                                    resetFormAddPhotoGallery();
                                    setShowAddPhotoGallery(false);
                                    notify.success(
                                      "Gallery image added successfully"
                                    );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Add Sponsorship
                            </Button>
                            <Button
                              onClick={() => setShowAddPhotoGallery(false)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </>
                      )}

                      <SponsorshipCard />
                    </>
                  ),
                },

                {
                  key: "6",
                  label: (
                    <span className={tabLabelClassName}>Communication</span>
                  ),
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formCommitteeLeadership}
                        schemaName={committeeLeadershipSchema || ""}
                        recordId={
                          isEditing &&
                          editingSchema === committeeLeadershipSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formCommitteeLeadershipRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing &&
                        editingSchema === committeeLeadershipSchema ? (
                          <>
                            <Button
                              type="primary"
                              loading={savingCommunication}
                              disabled={savingCommunication}
                              onClick={async () => {
                                if (savingCommunication) {
                                  return;
                                } // Prevent double-click
                                try {
                                  setSavingCommunication(true);
                                  if (id) {
                                    formCommitteeLeadership.setValues({
                                      jdid: id,
                                    });
                                  }
                                  const formValues =
                                    formCommitteeLeadership.values;
                                  const jdid = formValues.jdid;

                                  if (id && String(id) === String(jdid ?? "")) {
                                    await formCommitteeLeadershipRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormCommitteeLeadership();
                                    // notify.success(
                                    //   "Committee member updated successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                } finally {
                                  setSavingCommunication(false);
                                }
                              }}
                            >
                              Update Communication
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            loading={savingCommunication}
                            disabled={savingCommunication}
                            onClick={async () => {
                              if (savingCommunication) {
                                return;
                              } // Prevent double-click
                              try {
                                setSavingCommunication(true);
                                if (id) {
                                  formCommitteeLeadership.setValues({
                                    jdid: id,
                                  });
                                }
                                const formValues =
                                  formCommitteeLeadership.values;
                                const jdid = formValues.jdid;

                                if (id && String(id) === String(jdid ?? "")) {
                                  await formCommitteeLeadershipRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormCommitteeLeadership();
                                  // notify.success(
                                  //   "Committee member saved successfully"
                                  // );
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              } finally {
                                setSavingCommunication(false);
                              }
                            }}
                          >
                            Save Communication
                          </Button>
                        )}
                      </div>
                      <ConfigDynamicTable<any>
                        columns={columnCommittee}
                        key={refreshKey}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`Communications`}
                        tableName={committeeLeadershipSchema || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },

                {
                  key: "4",
                  label: <span className={tabLabelClassName}>QR Check-In</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formNoticeBoard}
                        schemaName={noticeBoardSchema || ""}
                        recordId={
                          isEditing && editingSchema === noticeBoardSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formNoticeBoardRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing && editingSchema === noticeBoardSchema ? (
                          <>
                            <Button
                              type="primary"
                              loading={savingQRCheckIn}
                              disabled={savingQRCheckIn}
                              onClick={async () => {
                                if (savingQRCheckIn) {
                                  return;
                                } // Prevent double-click
                                try {
                                  setSavingQRCheckIn(true);
                                  if (id) {
                                    formNoticeBoard.setValues({ jdid: id });
                                  }
                                  const formValues = formNoticeBoard.values;
                                  const jdid = formValues.jdid;

                                  if (id && String(id) === String(jdid ?? "")) {
                                    await formNoticeBoardRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormNoticeBoard();
                                    // notify.success(
                                    //   "Notice updated successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                } finally {
                                  setSavingQRCheckIn(false);
                                }
                              }}
                            >
                              Update QR Check-In
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            loading={savingQRCheckIn}
                            disabled={savingQRCheckIn}
                            onClick={async () => {
                              if (savingQRCheckIn) {
                                return;
                              } // Prevent double-click
                              try {
                                setSavingQRCheckIn(true);
                                if (id) {
                                  formNoticeBoard.setValues({ jdid: id });
                                }
                                const formValues = formNoticeBoard.values;
                                const jdid = formValues.jdid;

                                if (id && String(id) === String(jdid ?? "")) {
                                  await formNoticeBoardRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormNoticeBoard();
                                  // notify.success("Notice saved successfully");
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              } finally {
                                setSavingQRCheckIn(false);
                              }
                            }}
                          >
                            Save QR Check-In
                          </Button>
                        )}
                      </div>
                      <ConfigDynamicTable<any>
                        columns={columnsnoticeboard}
                        key={refreshKey}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`QR Check-Ins`}
                        tableName={noticeBoardSchema || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },
                {
                  key: "analytics",
                  label: <span className={tabLabelClassName}>Analytics</span>,
                  children: (
                    <div style={{ padding: "20px" }}>
                      {/* Analytics Summary Cards */}
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6} lg={6} xl={6}>
                          <Card
                            style={{
                              borderRadius: 16,
                              background: "hsl(var(--card))",
                            }}
                            bodyStyle={{ padding: "20px" }}
                          >
                            {/* Label */}
                            <p
                              style={{
                                fontSize: 16,
                                fontWeight: 500,
                                color: "hsl(var(--muted-foreground))",
                                margin: 0,
                                textAlign: "left",
                              }}
                            >
                              Upcoming Events
                            </p>
                            {/* Value */}
                            <h2
                              style={{
                                fontSize: 25,
                                fontWeight: 700,
                                color: "hsl(var(--primary))",
                                margin: "8px 0 0 0",
                                textAlign: "left",
                              }}
                            >
                              04
                            </h2>
                          </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={6} xl={6}>
                          <Card
                            style={{
                              borderRadius: 16,
                              background: "hsl(var(--card))",
                            }}
                            bodyStyle={{ padding: "20px" }}
                          >
                            {/* Label */}
                            <p
                              style={{
                                fontSize: 16,
                                fontWeight: 500,
                                color: "hsl(var(--muted-foreground))",
                                margin: 0,
                                textAlign: "left",
                              }}
                            >
                              Email Engagement
                            </p>
                            {/* Value */}
                            <h2
                              style={{
                                fontSize: 25,
                                fontWeight: 700,
                                color: "hsl(var(--primary))",
                                margin: "8px 0 0 0",
                                textAlign: "left",
                              }}
                            >
                              10 per day
                            </h2>
                          </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={6} xl={6}>
                          <Card
                            style={{
                              borderRadius: 16,
                              background: "hsl(var(--card))",
                            }}
                            bodyStyle={{ padding: "20px" }}
                          >
                            {/* Label */}
                            <p
                              style={{
                                fontSize: 16,
                                fontWeight: 500,
                                color: "hsl(var(--muted-foreground))",
                                margin: 0,
                                textAlign: "left",
                              }}
                            >
                              Calls Engagement
                            </p>
                            {/* Value */}
                            <h2
                              style={{
                                fontSize: 25,
                                fontWeight: 700,
                                color: "hsl(var(--primary))",
                                margin: "8px 0 0 0",
                                textAlign: "left",
                              }}
                            >
                              20 per day
                            </h2>
                          </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={6} xl={6}>
                          <Card
                            style={{
                              borderRadius: 16,
                              background: "hsl(var(--card))",
                            }}
                            bodyStyle={{ padding: "20px" }}
                          >
                            {/* Label */}
                            <p
                              style={{
                                fontSize: 16,
                                fontWeight: 500,
                                color: "hsl(var(--muted-foreground))",
                                margin: 0,
                                textAlign: "left",
                              }}
                            >
                              Meetings
                            </p>
                            {/* Value */}
                            <h2
                              style={{
                                fontSize: 25,
                                fontWeight: 700,
                                color: "hsl(var(--primary))",
                                margin: "8px 0 0 0",
                                textAlign: "left",
                              }}
                            >
                              20 per day
                            </h2>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  ),
                },
              ]
            : [
                {
                  key: "1",
                  label: (
                    <span className={tabLabelClassName}>Event Information</span>
                  ),
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formOverview}
                        schemaName={basicInfoSchema ?? ""}
                        ref={formOverviewRef}
                      />
                      <div className="flex gap-4">
                        <Button type="primary" onClick={handleSaveOrganization}>
                          Save
                        </Button>
                      </div>
                    </>
                  ),
                },
              ]
        }
      />
    </div>
  );
};

export default EventsPage;
